
import React, { useState, useEffect, useRef } from 'react';
import { StorageService } from '../services/storageService';
import { College, Assessment, Question, CandidateAttempt, AttemptStatus, SystemSettings, AssessmentLink } from '../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'colleges' | 'assessments' | 'reports' | 'customization' | 'settings' | 'links'>('overview');
  const [colleges, setColleges] = useState<College[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [attempts, setAttempts] = useState<CandidateAttempt[]>([]);
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [links, setLinks] = useState<AssessmentLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // State for new assessment form
  const [showCreateAssessment, setShowCreateAssessment] = useState(false);
  const [newAsm, setNewAsm] = useState<Partial<Assessment>>({
    name: '',
    duration: 30,
    maxViolations: 3,
    questionCount: 0,
    resumePin: 'mh-123',
    isActive: true,
    welcomeText: 'Welcome to the assessment.',
    bufferTime: 20,
    allowedColleges: []
  });

  const [createErrors, setCreateErrors] = useState<{ [key: string]: string }>({});
  const [tempUploadQuestions, setTempUploadQuestions] = useState<Question[]>([]);



  const [editingAssessment, setEditingAssessment] = useState<Assessment | null>(null);
  const [editingQuestions, setEditingQuestions] = useState<Question[]>([]);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [tempQuestion, setTempQuestion] = useState<Question | null>(null);

  const [editErrors, setEditErrors] = useState<{ [key: string]: string }>({});
  const [questionSearch, setQuestionSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // College Mgmt State
  const [newCollegeName, setNewCollegeName] = useState('');
  const [newCollegeId, setNewCollegeId] = useState('');
  const [tempStudentList, setTempStudentList] = useState<{ id: string; name?: string; department?: string }[]>([]);
  const [editingCollege, setEditingCollege] = useState<College | null>(null);
  const [originalEditingId, setOriginalEditingId] = useState<string | null>(null);
  const [editingStudentId, setEditingStudentId] = useState<string | null>(null);
  const [tempStudent, setTempStudent] = useState<{ id: string; name?: string; department?: string } | null>(null);

  const [showCreateCollege, setShowCreateCollege] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');

  const [sortConfig, setSortConfig] = useState<{ key: 'candidateName' | 'studentId' | 'score'; direction: 'asc' | 'desc' }>({ key: 'candidateName', direction: 'asc' });
  const [reportFilter, setReportFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [verdictFilter, setVerdictFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Settings State
  const [newRecruitPin, setNewRecruitPin] = useState('');
  const [settingsForm, setSettingsForm] = useState<SystemSettings>({ recruitAccessPin: '' });
  const [selectedCollege, setSelectedCollege] = useState('');
  const [assessmentFilter, setAssessmentFilter] = useState('');

  // Link Generation State
  const [newLinkSlug, setNewLinkSlug] = useState('');
  const [linkCollegeId, setLinkCollegeId] = useState('');
  const [linkAssessmentId, setLinkAssessmentId] = useState('');

  // Report Detail State
  const [selectedReport, setSelectedReport] = useState<CandidateAttempt | null>(null);
  const [reportQuestions, setReportQuestions] = useState<Question[]>([]);
  const [loadingReportId, setLoadingReportId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    ids?: string[];
    type: 'college' | 'assessment' | 'attempt' | 'link' | 'bulk_attempt';
    title: string;
    description: string;
  } | null>(null);

  const [selectedReportIds, setSelectedReportIds] = useState<Set<string>>(new Set());


  const fileInputRef = useRef<HTMLInputElement>(null);
  const studentListRef = useRef<HTMLDivElement>(null);
  const questionListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    refreshData();
  }, []);

  // Poll for updates on active reports (Live Monitoring)
  useEffect(() => {
    let interval: any;
    if (selectedReport && selectedReport.status === AttemptStatus.IN_PROGRESS) {
      interval = setInterval(async () => {
        const updated = await StorageService.getAttempt(selectedReport.id);
        if (updated) {
          setSelectedReport(updated);
          // Also update the main list to keep it in sync
          setAttempts(prev => prev.map(a => a.id === updated.id ? updated : a));
        }
      }, 5000); // Poll every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [selectedReport?.id, selectedReport?.status]);

  const refreshData = async (silent = false) => {
    if (!silent) setIsLoading(true);
    const [c, a, att, s, links] = await Promise.all([
      StorageService.getColleges(),
      StorageService.getAssessments(),
      StorageService.getAllAttempts(),
      StorageService.getSettings(),
      StorageService.getAccessLinks()
    ]);

    // Check for Stuck/Abandoned Attempts (Duration + Buffer Exceeded)
    const now = Date.now();
    const updates: Promise<void>[] = [];
    let updatedAttempts = [...att];

    updatedAttempts = updatedAttempts.map(attempt => {
      if (attempt.status === AttemptStatus.IN_PROGRESS) {
        const asm = a.find(x => x.id === attempt.assessmentId);
        if (asm) {
          const buffer = asm.bufferTime || 20; // Default 20 mins buffer
          const limit = attempt.startTime + ((asm.duration + buffer) * 60 * 1000);
          if (now > limit) {
            // Mark as ABANDONED
            updates.push(StorageService.updateAttempt({ ...attempt, status: AttemptStatus.ABANDONED }));
            return { ...attempt, status: AttemptStatus.ABANDONED };
          }
        }
      }
      return attempt;
    });

    if (updates.length > 0) {
      console.log(`Auto-abandoned ${updates.length} stale attempts.`);
      // We don't await updates here to speed up UI, they happen in background
      Promise.all(updates).catch(e => console.error("Failed to update abandoned status", e));
    }

    setColleges(c);
    setAssessments(a);
    setAttempts(updatedAttempts); // Use updated list
    setSettings(s);
    setLinks(links);
    if (s) setSettingsForm(s);
    setIsLoading(false);
  };

  const handleAddCollege = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCollegeName.trim()) return;

    const idToCheck = newCollegeId.trim() || 'c_' + Date.now();
    if (colleges.find(c => c.id === idToCheck)) {
      alert('College ID already exists!');
      return;
    }

    // setIsLoading(true); // Removed global loader
    await StorageService.saveCollege({
      id: idToCheck,
      name: newCollegeName.trim(),
      isEnabled: true,
      participantCount: 0,
      allowedStudents: tempStudentList
    });
    setNewCollegeName('');
    setNewCollegeId('');
    setTempStudentList([]);
    setShowCreateCollege(false);
    await refreshData(true);
  };

  const downloadStudentTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      { "Student ID": "Example123", "Student Name": "John Doe", "Department": "CSE" },
      { "Student ID": "Example124", "Student Name": "Jane Smith", "Department": "ECE" }
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");
    XLSX.writeFile(wb, "Student_List_Template.xlsx");
  };

  const downloadQuestionTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      {
        "question": "What is the complexity of binary search?",
        "optionA": "O(n)",
        "optionB": "O(log n)",
        "optionC": "O(n^2)",
        "optionD": "O(1)",
        "correctOption": "B",
        "marks": 1,
        "category": "Algorithms"
      },
      {
        "question": "Which data structure uses LIFO?",
        "optionA": "Queue",
        "optionB": "Array",
        "optionC": "Stack",
        "optionD": "Tree",
        "correctOption": "C",
        "marks": 1,
        "category": "Data Structures"
      }
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Questions");
    XLSX.writeFile(wb, "Question_Bank_Template.xlsx");
  };



  const handleStudentListImport = async (e: React.ChangeEvent<HTMLInputElement>, isEditMode: boolean = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const bstr = event.target?.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws) as any[];

      let duplicatesCount = 0;
      let invalidCount = 0;
      const validStudents: { id: string; name: string; department?: string }[] = [];
      const skippedRecords: any[] = [];

      // Get current list to check duplicates against

      // Get current list to check duplicates against
      const currentList = isEditMode && editingCollege ? (editingCollege.allowedStudents || []) : tempStudentList;
      const existingIds = new Set(currentList.map(s => s.id.toLowerCase()));

      data.forEach(row => {
        const id = (row['Student ID'] || row['id'] || row['ID'] || Object.values(row)[0] || '').toString().trim();
        const name = (row['Student Name'] || row['name'] || row['Name'] || Object.values(row)[1] || '').toString().trim();
        const dept = (row['Department'] || row['department'] || row['Dept'] || '').toString().trim();

        if (!id || !name || !dept) {
          invalidCount++;
          skippedRecords.push({ ...row, Reason: 'Missing ID, Name or Department' });
          return;
        }

        if (existingIds.has(id.toLowerCase())) {
          duplicatesCount++;
          skippedRecords.push({ ...row, Reason: 'Duplicate ID (Already exists in list)' });
          return;
        }

        // Also check if multiple rows in THIS file have same ID
        if (validStudents.find(s => s.id.toLowerCase() === id.toLowerCase())) {
          duplicatesCount++;
          skippedRecords.push({ ...row, Reason: 'Duplicate ID (Duplicate within file)' });
          return;
        }

        validStudents.push({ id, name, department: dept });
      });

      if (validStudents.length > 0) {
        if (isEditMode && editingCollege) {
          setEditingCollege({ ...editingCollege, allowedStudents: [...(editingCollege.allowedStudents || []), ...validStudents] });
        } else {
          setTempStudentList(prev => [...prev, ...validStudents]);
        }
      }

      let msg = `Loaded ${validStudents.length} students.`;
      if (duplicatesCount > 0) msg += `\nSkipped ${duplicatesCount} duplicates.`;
      if (invalidCount > 0) msg += `\nSkipped ${invalidCount} invalid rows (missing ID, Name, or Dept).`;

      if ((duplicatesCount + invalidCount) > 0) {
        msg += `\n\nDownload error report?`;
        if (window.confirm(msg)) {
          const ws = XLSX.utils.json_to_sheet(skippedRecords);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "Skipped");
          XLSX.writeFile(wb, "Student_Import_Errors.xlsx");
        }
      } else {
        alert(msg);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = ''; // Reset input
  };

  const handleDeleteCollege = async (id: string) => {
    const college = colleges.find(c => c.id === id);
    setConfirmDelete({
      id,
      type: 'college',
      title: 'Delete College?',
      description: `You are about to delete "${college?.name || id}". This will permanently remove the college record and might orphan existing student records.`
    });
  };

  const handleEditCollegeSave = async () => {
    if (!editingCollege || !originalEditingId) return;

    // setIsLoading(true);
    if (originalEditingId !== editingCollege.id) {
      // ID Changed
      if (colleges.find(c => c.id === editingCollege.id)) {
        alert('New ID already exists with this name!');
        // setIsLoading(false);
        return;
      }
      await StorageService.saveCollege({
        ...editingCollege,
        id: editingCollege.id,
        name: editingCollege.name
      });
      await StorageService.deleteCollege(originalEditingId);
    } else {
      await StorageService.saveCollege(editingCollege);
    }
    setEditingCollege(null);
    setOriginalEditingId(null);
    await refreshData(true);
  };

  const handleSettingsSave = async () => {
    // setIsLoading(true);
    await StorageService.updateSettings(settingsForm);
    alert('Settings updated successfully!');
    setSettings(settingsForm);
    // setIsLoading(false);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 500000) { // 500KB limit
        alert('Image too large (Max 500KB)');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setSettingsForm(prev => ({ ...prev, studentLoginImage: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };


  const handleCreateAssessment = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset Errors
    setCreateErrors({});
    let hasError = false;
    const errors: typeof createErrors = {};

    if (!newAsm.name?.trim()) {
      errors.name = "Assessment Name is required";
      hasError = true;
    }

    if (tempUploadQuestions.length === 0) {
      errors.file = "Please upload a question file (Excel/CSV)";
      hasError = true;
    }

    if (tempUploadQuestions.length > 0 && (newAsm.questionCount || 0) > tempUploadQuestions.length) {
      errors.questions = `Cannot exceed ${tempUploadQuestions.length} available questions`;
      hasError = true;
    }

    if (tempUploadQuestions.length > 0 && (newAsm.questionCount || 0) <= 0) {
      errors.questions = "Question count must be greater than 0";
      hasError = true;
    }

    if (!newAsm.resumePin?.trim()) {
      errors.resumePin = "Resume PIN is required";
      hasError = true;
    }

    if ((newAsm.duration || 0) <= 0) {
      errors.duration = "Duration must be > 0";
      hasError = true;
    }

    if ((newAsm.maxViolations || 0) < 0) {
      errors.maxViolations = "Violations cannot be negative";
      hasError = true;
    }

    if (hasError) {
      setCreateErrors(errors);
      return;
    }

    // setIsLoading(true);
    const assessmentId = 'a_' + Date.now();
    const assessment: Assessment = {
      id: assessmentId,
      name: newAsm.name!,
      duration: newAsm.duration || 30,
      questionCount: newAsm.questionCount || tempUploadQuestions.length,
      resumePin: newAsm.resumePin || 'mh-123',
      maxViolations: newAsm.maxViolations || 3,
      allowedColleges: colleges.map(c => c.id),
      isActive: true,
      bufferTime: newAsm.bufferTime || 20, // Default 20 mins
      welcomeText: newAsm.welcomeText || '',
      logoUrl: ''
    };

    await StorageService.saveAssessment(assessment);

    // Save Questions with new ID
    const savePromises = tempUploadQuestions.map(q =>
      StorageService.saveQuestion({ ...q, assessmentId: assessmentId, id: `q_${assessmentId}_${Math.random().toString(36).substr(2, 9)}` })
    );
    await Promise.all(savePromises);

    setShowCreateAssessment(false);
    setNewAsm({ name: '', duration: 30, maxViolations: 3, resumePin: 'mh-123', isActive: true, questionCount: 0, bufferTime: 20 });
    setTempUploadQuestions([]);
    setCreateErrors({});
    await refreshData(true);
  };

  const handleNewAssessmentFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const bstr = event.target?.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws) as any[];

      const validQuestions: Question[] = [];
      const seenTexts = new Set<string>();
      const skippedRecords: any[] = [];

      data.forEach((row: any, idx) => {
        const text = (row.question || '').toString().trim();
        const optionA = (row.optionA || '').toString().trim();
        const optionB = (row.optionB || '').toString().trim();
        const correctOption = (row.correctOption || '').toString().trim().toUpperCase();

        if (!text || !optionA || !optionB || !correctOption) {
          skippedRecords.push({ ...row, Reason: 'Missing Text, Options, or Correct Answer' });
          return;
        }
        if (seenTexts.has(text.toLowerCase())) {
          skippedRecords.push({ ...row, Reason: 'Duplicate Question' });
          return;
        }

        seenTexts.add(text.toLowerCase());
        validQuestions.push({
          id: `q_temp_${Date.now()}_${idx}`,
          assessmentId: '',
          text: text,
          options: {
            A: optionA,
            B: optionB,
            C: (row.optionC || '').toString().trim(),
            D: (row.optionD || '').toString().trim()
          },
          correctOption: correctOption as any,
          marks: Number(row.marks) || 1,
          category: (row.category || row.type || '').toString().trim()
        });
      });

      setTempUploadQuestions(validQuestions);
      setNewAsm(prev => ({ ...prev, questionCount: validQuestions.length }));
      setCreateErrors(prev => ({ ...prev, file: '' }));

      const skippedCount = data.length - validQuestions.length;
      let msg = `Loaded ${validQuestions.length} unique valid questions.`;
      if (skippedCount > 0) {
        msg += `\nSkipped ${skippedCount} items. Download report?`;
        if (window.confirm(msg)) {
          const ws = XLSX.utils.json_to_sheet(skippedRecords);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "Skipped");
          XLSX.writeFile(wb, "Question_Import_Errors.xlsx");
        }
      } else {
        alert(msg);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };











  const handleDeleteAssessment = async (id: string) => {
    const asm = assessments.find(a => a.id === id);
    setConfirmDelete({
      id,
      type: 'assessment',
      title: 'Delete Assessment?',
      description: `You are about to delete "${asm?.name || id}". This will permanently remove the assessment and all its associated questions.`
    });
  };

  const handleEditAssessment = async (a: Assessment) => {
    // setIsLoading(true);
    setEditingAssessment(a);
    const qs = await StorageService.getQuestions(a.id);
    setEditingQuestions(qs);
    setEditErrors({});
    // setIsLoading(false);
  };

  const handleSaveEditedAssessment = async () => {
    if (!editingAssessment) return;

    const errors: { [key: string]: string } = {};
    let hasError = false;

    if (!editingAssessment.name?.trim()) {
      errors.name = "Assessment Name is required";
      hasError = true;
    }
    if (!editingAssessment.resumePin?.trim()) {
      errors.resumePin = "Resume PIN is required";
      hasError = true;
    }
    if ((editingAssessment.duration || 0) <= 0) {
      errors.duration = "Duration must be > 0";
      hasError = true;
    }
    const qCount = editingAssessment.questionCount || 0;
    if (qCount <= 0) {
      errors.questionCount = "Must be at least 1";
      hasError = true;
    }
    if (qCount > editingQuestions.length) {
      errors.questionCount = `Max available is ${editingQuestions.length}`;
      hasError = true;
    }

    if ((editingAssessment.maxViolations || 0) < 0) {
      errors.maxViolations = "Cannot be negative";
      hasError = true;
    }

    if (hasError) {
      setEditErrors(errors);
      return;
    }

    // setIsLoading(true);
    await StorageService.saveAssessment(editingAssessment);
    setEditErrors({});
    setEditingAssessment(null);
    await refreshData(true);
  };

  const handleExcelImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingAssessment) return;

    // setIsLoading(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      const bstr = event.target?.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws) as any[];

      const existingQuestionsMap = new Map<string, Question>(editingQuestions.map(q => [q.text.toLowerCase().trim(), q]));
      const questionsToSave: Question[] = [];
      const skippedRecords: any[] = [];
      let newCount = 0;
      let updateCount = 0;
      let invalidCount = 0;

      data.forEach((row: any, idx) => {
        const text = (row.question || '').toString().trim();
        const optionA = (row.optionA || '').toString().trim();
        const optionB = (row.optionB || '').toString().trim();
        const correctOption = (row.correctOption || '').toString().trim().toUpperCase();

        if (!text || !optionA || !optionB || !correctOption) {
          invalidCount++;
          skippedRecords.push({ ...row, Reason: 'Missing Required Fields' });
          return;
        }

        const existingQuestion = existingQuestionsMap.get(text.toLowerCase());
        const questionId = existingQuestion ? existingQuestion.id : `q_imp_${Date.now()}_${idx}`;

        const questionData: Question = {
          id: questionId,
          assessmentId: editingAssessment.id,
          text: text,
          options: {
            A: optionA,
            B: optionB,
            C: (row.optionC || '').toString().trim(),
            D: (row.optionD || '').toString().trim()
          },
          correctOption: correctOption as any,
          marks: Number(row.marks) || 1,
          category: (row.category || row.type || '').toString().trim()
        };

        questionsToSave.push(questionData);

        if (existingQuestion) {
          updateCount++;
        } else {
          newCount++;
          // Update upsert map to handle duplicates within the same file??
          // Ideally we should process the file sequentially, so subsequent rows with same text update the previous one in this batch too.
          existingQuestionsMap.set(text.toLowerCase(), questionData);
        }
      });

      if (questionsToSave.length > 0) {
        const savePromises = questionsToSave.map(q => StorageService.saveQuestion(q));
        await Promise.all(savePromises);
        await handleEditAssessment(editingAssessment);
      }

      // setIsLoading(false);

      let msg = `Process Complete:\n• Created ${newCount} new questions\n• Updated ${updateCount} existing questions`;
      if (invalidCount > 0) {
        msg += `\n• Skipped ${invalidCount} invalid rows (missing fields)`;
      }

      if (invalidCount > 0) {
        msg += `\n\nDownload error report for skipped items?`;
        if (window.confirm(msg)) {
          const ws = XLSX.utils.json_to_sheet(skippedRecords);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "Skipped");
          XLSX.writeFile(wb, "Import_Errors.xlsx");
        }
      } else {
        alert(msg);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  const handleExportExcel = async () => {
    setIsLoading(true);
    try {
      // 1. Fetch all questions needed to map categories
      const assessmentIds = Array.from(new Set(sortedAttempts.map(a => a.assessmentId)));
      const allQuestionsMap = new Map<string, Question>();

      await Promise.all(assessmentIds.map(async (aid) => {
        const qs = await StorageService.getQuestions(aid);
        qs.forEach(q => allQuestionsMap.set(q.id, q));
      }));

      const data = sortedAttempts.map(a => {
        const duration = a.endTime ? Math.floor((a.endTime - a.startTime) / 60000) + 'm ' + Math.floor(((a.endTime - a.startTime) % 60000) / 1000) + 's' : '-';

        // Calculate Category Scores
        const catScores: Record<string, number> = {};

        Object.entries(a.responses).forEach(([qId, selectedOpt]) => {
          const q = allQuestionsMap.get(qId);
          if (q) {
            const cat = q.category || 'General';
            if (!catScores[cat]) { catScores[cat] = 0; }

            if (selectedOpt === q.correctOption) {
              catScores[cat] += q.marks;
            }
          }
        });

        // Flatten categories into the row
        const catColumns: Record<string, number> = {};
        Object.keys(catScores).forEach(cat => {
          catColumns[`${cat} Score`] = catScores[cat];
        });

        return {
          'Candidate Name': a.candidateName,
          'Student ID': a.studentId,
          'Department': a.department || colleges.find(c => c.id === a.collegeId)?.allowedStudents?.find(s => s.id === a.studentId)?.department || '-',
          'College': a.collegeName || colleges.find(c => c.id === a.collegeId)?.name || `ID: ${a.collegeId}`,
          'Score': a.score ?? 0,
          ...catColumns,
          'Time Taken': duration,
          'Violations': a.violations,
          'Verdict': getVerdict(a).label,
          'Status': a.status,
          'Date': new Date(a.startTime).toLocaleDateString(),
          'Time': new Date(a.startTime).toLocaleTimeString()
        };
      });

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Reports");
      XLSX.writeFile(wb, `Reports_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error("Export Error: ", err);
      alert("Failed to export report");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportColleges = () => {
    const data = colleges.map(c => ({
      'College Name': c.name,
      'College ID': c.id,
      'Enabled': c.isEnabled !== false ? 'Yes' : 'No',
      'Student Count': c.allowedStudents?.length || 0
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Institutions");
    XLSX.writeFile(wb, `Institutions_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleExportCollegeStudents = (college: College) => {
    if (!college.allowedStudents?.length) {
      alert("No students to export");
      return;
    }

    const data = college.allowedStudents.map(s => ({
      'Student ID': s.id,
      'Name': s.name,
      'Department': s.department || '-'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Student_List");
    XLSX.writeFile(wb, `Students_${college.name.replace(/\s+/g, '_')}.xlsx`);
  };

  const handleExportQuestions = () => {
    if (!editingQuestions || editingQuestions.length === 0) {
      alert("No questions to export");
      return;
    }
    const data = editingQuestions.map(q => ({
      'question': q.text,
      'optionA': q.options.A,
      'optionB': q.options.B,
      'optionC': q.options.C,
      'optionD': q.options.D,
      'correctOption': q.correctOption,
      'marks': q.marks,
      'category': q.category
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Questions");
    XLSX.writeFile(wb, `${editingAssessment?.name || 'Assessment'}_Questions.xlsx`);
  };

  const getVerdict = (a: CandidateAttempt) => {
    if (a.status === AttemptStatus.IN_PROGRESS) return { label: 'ON-TEST', class: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-900/30' };
    if (a.status === AttemptStatus.LOCKED) return { label: 'TERMINATED', class: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-500 border-red-200' };
    if (a.status === AttemptStatus.ABANDONED) return { label: 'GAVE UP', class: 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200' };

    if (!a.score && a.score !== 0) return { label: 'PENDING', class: 'bg-gray-100 text-gray-500' };
    const assessment = assessments.find(as => as.id === a.assessmentId);
    if (!assessment) return { label: 'N/A', class: 'bg-gray-100 text-gray-500' };

    const totalPossible = assessment.questionCount;
    const scorePerc = (a.score / (totalPossible || 1)) * 100;
    const hasHighViolations = a.violations >= 3;

    if (scorePerc >= 75 && !hasHighViolations) return { label: 'POTENTIAL', class: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-500 border-green-200' };
    if (hasHighViolations) return { label: 'RISK', class: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-500 border-red-200' };
    if (scorePerc < 40) return { label: 'REJECT', class: 'bg-red-50 dark:bg-gray-800 text-gray-700 dark:text-gray-400 border-gray-200' };
    return { label: 'REVIEW', class: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-500 border-amber-200' };
  };

  const sortedAttempts = [...attempts]
    .filter(a => {
      const matchesStatus = !statusFilter || a.status === AttemptStatus[statusFilter as keyof typeof AttemptStatus];
      const matchesCollege = !selectedCollege || a.collegeId === selectedCollege;
      const matchesSearch = !reportFilter ||
        a.candidateName.toLowerCase().includes(reportFilter.toLowerCase()) ||
        a.studentId.toLowerCase().includes(reportFilter.toLowerCase());

      const matchesAssessment = !assessmentFilter || a.assessmentId === assessmentFilter;

      let matchesVerdict = true;
      if (verdictFilter) {
        const v = getVerdict(a);
        matchesVerdict = v.label === verdictFilter;
      }

      return matchesStatus && matchesCollege && matchesSearch && matchesVerdict && matchesAssessment;
    })
    .sort((a, b) => {
      let valA, valB;
      if (sortConfig.key === 'score') {
        valA = a.score ?? 0;
        valB = b.score ?? 0;
      } else if (sortConfig.key === 'studentId') {
        valA = a.studentId.toLowerCase();
        valB = b.studentId.toLowerCase();
      } else {
        valA = a.candidateName.toLowerCase();
        valB = b.candidateName.toLowerCase();
      }
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

  // Pagination calculations
  const totalPages = Math.ceil(sortedAttempts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAttempts = sortedAttempts.slice(startIndex, endIndex);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSelected = new Set(selectedReportIds);
    if (e.target.checked) {
      paginatedAttempts.forEach(a => newSelected.add(a.id));
    } else {
      paginatedAttempts.forEach(a => newSelected.delete(a.id));
    }
    setSelectedReportIds(newSelected);
  };

  const handleSelectReport = (id: string) => {
    const newSelected = new Set(selectedReportIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedReportIds(newSelected);
  };

  const toggleSort = (key: 'candidateName' | 'studentId' | 'score') => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleViewReport = async (attemptArg: CandidateAttempt) => {
    setLoadingReportId(attemptArg.id);
    try {
      // 1. Fetch fresh attempt data to ensure we have the full object (including questionOrder)
      // The list view might have stale or partial data
      const fullAttempt = await StorageService.getAttempt(attemptArg.id) || attemptArg;

      let qs = await StorageService.getQuestions(fullAttempt.assessmentId);

      // 2. Determine which questions to show
      let filterIds = fullAttempt.questionOrder;

      // Fallback: If questionOrder is missing (legacy data), use the keys from responses
      if (!filterIds || filterIds.length === 0) {
        if (fullAttempt.responses) {
          filterIds = Object.keys(fullAttempt.responses);
        }
      }

      // 3. Apply Filter
      if (filterIds && filterIds.length > 0) {
        qs = qs.filter(q => filterIds?.includes(q.id));
      }

      setReportQuestions(qs);
      setSelectedReport(fullAttempt);
    } catch (err) {
      console.error(err);
      alert('Failed to load report details');
    } finally {
      setLoadingReportId(null);
    }
  };

  if (isLoading && !editingAssessment && !showCreateAssessment && !selectedReport) {
    return (
      <div className="p-20 flex flex-col items-center justify-center">
        <div className="loader"></div>
        <p className="mt-4 text-xs font-black text-gray-400 uppercase tracking-widest">Updating from Cloud...</p>
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-10 py-6">
      <div className="flex flex-col lg:flex-row gap-10">
        {/* Sidebar Nav */}
        <div className="w-full lg:w-[20%] space-y-2 lg:sticky lg:top-24 lg:self-start">
          <div className="px-4 py-6">
            <h2 className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] mb-4">Control Center</h2>
            <nav className="space-y-1">
              {[
                { id: 'overview', label: '📊 Dashboard Overview' },
                { id: 'colleges', label: '🏫 College Management' },
                { id: 'assessments', label: '⚙️ Assessments & Q-Bank' },
                { id: 'links', label: '🔗 Generate Link' },
                { id: 'reports', label: '📄 Student Reports' },
                { id: 'settings', label: '🔒 Security Settings' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id as any);
                    setEditingAssessment(null);
                    setShowCreateAssessment(false);
                    setEditingCollege(null);
                    setShowCreateCollege(false);
                    refreshData(true); // Fetch updated records on tab switch
                  }}
                  className={`w-full text-left px-5 py-4 rounded-2xl font-bold transition-all transform active:scale-95 ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-100 dark:shadow-none' : 'text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 hover:text-indigo-600 dark:hover:text-indigo-400'}`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 bg-white dark:bg-gray-900 rounded-[2rem] shadow-sm border border-gray-100 dark:border-gray-800 relative overflow-hidden h-[calc(100vh-140px)] transition-colors">
          {activeTab === 'overview' && (
            <div className="flex flex-col h-full overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 z-10 shadow-sm">
                <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white transition-colors">Dashboard Overview</h3>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 p-8 rounded-[2rem] text-white shadow-xl shadow-indigo-100 dark:shadow-none">
                    <p className="text-indigo-100 text-[11px] font-black uppercase tracking-widest opacity-80">Total Candidates</p>
                    <h3 className="text-4xl font-black mt-2">{attempts.length}</h3>
                  </div>
                  <div className="bg-white dark:bg-gray-800 border-2 border-gray-50 dark:border-gray-700 p-8 rounded-[2rem] transition-colors">
                    <p className="text-gray-400 dark:text-gray-500 text-[11px] font-black uppercase tracking-widest">Successful Submissions</p>
                    <h3 className="text-4xl font-black text-gray-900 dark:text-white mt-2 transition-colors">{attempts.filter(a => a.status === AttemptStatus.SUBMITTED).length}</h3>
                  </div>
                  <div className="bg-white dark:bg-gray-800 border-2 border-gray-50 dark:border-gray-700 p-8 rounded-[2rem] transition-colors">
                    <p className="text-red-400 dark:text-red-500/70 text-[11px] font-black uppercase tracking-widest">Infractions Recorded</p>
                    <h3 className="text-4xl font-black text-red-600 dark:text-red-500 mt-2 transition-colors">{attempts.reduce((sum, a) => sum + a.violations, 0)}</h3>
                  </div>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  <div className="bg-gray-50/50 dark:bg-gray-800/20 p-8 rounded-[2rem] border border-gray-100 dark:border-gray-800">
                    <h4 className="text-xl font-black text-gray-900 dark:text-white mb-6 transition-colors">Institution Performance</h4>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={colleges.map(c => ({
                          ...c,
                          participantCount: attempts.filter(a => a.collegeId === c.id).length
                        })).filter(c => c.participantCount > 0)}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                          <XAxis dataKey="name" fontSize={10} tickLine={false} axisLine={false} tick={{ fill: '#94a3b8' }} />
                          <YAxis fontSize={10} tickLine={false} axisLine={false} tick={{ fill: '#94a3b8' }} />
                          <Tooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} cursor={{ fill: 'transparent' }} />
                          <Bar dataKey="participantCount" fill="#4f46e5" radius={[10, 10, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="bg-gray-50/50 dark:bg-gray-800/20 p-8 rounded-[2rem] border border-gray-100 dark:border-gray-800">
                    <h4 className="text-xl font-black text-gray-900 dark:text-white mb-6 transition-colors">Assessment Performance</h4>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={assessments.map(asm => ({
                          name: asm.name,
                          count: attempts.filter(a => a.assessmentId === asm.id).length
                        })).filter(x => x.count > 0)}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                          <XAxis dataKey="name" fontSize={10} tickLine={false} axisLine={false} tick={{ fill: '#94a3b8' }} />
                          <YAxis fontSize={10} tickLine={false} axisLine={false} tick={{ fill: '#94a3b8' }} />
                          <Tooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} cursor={{ fill: 'transparent' }} />
                          <Bar dataKey="count" fill="#8b5cf6" radius={[10, 10, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'colleges' && (
            <div className="flex flex-col h-full overflow-hidden">
              {!showCreateCollege && !editingCollege && (
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 z-10 shadow-sm flex items-center justify-between transition-colors">
                  <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white transition-colors">Institutions</h3>
                  <div className="flex items-center gap-2">

                    <button
                      onClick={() => {
                        setNewCollegeName('');
                        setNewCollegeId('');
                        setTempStudentList([]);
                        setShowCreateCollege(true);
                      }}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-xl font-bold text-xs shadow-lg shadow-indigo-100 dark:shadow-none hover:bg-indigo-700 transition-all"
                    >
                      + Add College
                    </button>
                  </div>
                </div>
              )}
              <div className={`flex-1 overflow-y-auto ${showCreateCollege || editingCollege ? 'p-0' : 'p-5'} dark:bg-gray-900 custom-scrollbar`}>
                {showCreateCollege ? (
                  <div className="flex flex-col h-full overflow-hidden">
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-4 bg-white dark:bg-gray-900 sticky top-0 z-20">
                      <div className="flex items-center gap-3">
                        <button onClick={() => setShowCreateCollege(false)} className="p-2 rounded-xl text-gray-500 hover:bg-white dark:hover:bg-gray-800 shadow-sm transition-all">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Add New College</h3>
                      </div>
                    </div>

                    <div className="flex-1 overflow-hidden bg-white dark:bg-gray-900 flex flex-col">
                      <div className="flex-1 overflow-hidden p-4 md:p-6">
                        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
                          {/* LEFT: Form Config */}
                          <div className="flex flex-col h-full overflow-hidden">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col h-full overflow-y-auto custom-scrollbar">
                              <h4 className="text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-4 flex-shrink-0">College Details</h4>

                              <div className="space-y-4 flex-1">
                                <div>
                                  <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-1.5">College ID <span className="text-red-500">*</span></label>
                                  <input
                                    type="text"
                                    placeholder="e.g. anna-univ-2024"
                                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border-2 dark:border-gray-700 rounded-xl font-mono text-sm text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                                    value={newCollegeId}
                                    onChange={(e) => setNewCollegeId(e.target.value)}
                                  />
                                  <p className="text-[10px] text-gray-400 mt-1 ml-1">Unique identifier (no spaces recommended).</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-1.5">College Name <span className="text-red-500">*</span></label>
                                  <input
                                    type="text"
                                    placeholder="e.g. Anna University"
                                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border-2 dark:border-gray-700 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                                    value={newCollegeName}
                                    onChange={(e) => setNewCollegeName(e.target.value)}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-1.5">Upload Student List (Excel/CSV)</label>
                                  <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group cursor-pointer relative flex items-center justify-center">
                                    <input
                                      type="file"
                                      accept=".xlsx, .csv"
                                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                      id="newCollegeStudentUpload"
                                      onChange={(e) => handleStudentListImport(e, false)}
                                    />
                                    <div className="flex items-center gap-3">
                                      <div className="w-9 h-9 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg flex items-center justify-center text-lg group-hover:scale-110 transition-transform">
                                        📂
                                      </div>
                                      <div className="text-left">
                                        <div>
                                          <span className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline text-xs">Click to upload</span>
                                          <span className="text-gray-400 text-[10px] font-medium"> or drag & drop</span>
                                        </div>
                                        <p className="text-[9px] text-gray-400 font-mono">Supports .xlsx or .csv</p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex justify-between items-center mt-2">
                                    <p className="text-[10px] font-bold text-gray-400">{tempStudentList.length > 0 ? `✅ ${tempStudentList.length} students ready` : 'No file selected'}</p>
                                    <button onClick={downloadStudentTemplate} className="text-[10px] font-bold text-indigo-500 hover:text-indigo-600 flex items-center gap-1 py-1 px-2 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors">
                                      ⬇️ Download Sample Template
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <div className="flex gap-3 pt-4 mt-6 border-t border-gray-100 dark:border-gray-700 flex-shrink-0">
                                <button onClick={() => setShowCreateCollege(false)} className="flex-1 px-4 py-3 font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all text-sm">Cancel</button>
                                <button
                                  onClick={handleAddCollege}
                                  disabled={!newCollegeName.trim() || !newCollegeId.trim()}
                                  className="flex-[2] bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-indigo-100 dark:shadow-none hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
                                >
                                  Create College
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* RIGHT: Preview */}
                          <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-4 md:p-6 border border-gray-100 dark:border-gray-800 overflow-hidden">
                            <div className="flex items-center justify-between mb-4 flex-shrink-0">
                              <h4 className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                                Student Preview {tempStudentList.length > 0 && <span className="text-indigo-600 dark:text-indigo-400">({tempStudentList.length})</span>}
                              </h4>
                              {tempStudentList.length > 0 && (
                                <button onClick={() => setTempStudentList([])} className="text-[10px] font-bold text-red-500 hover:bg-red-50 px-2 py-1 rounded">Clear List</button>
                              )}
                            </div>

                            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar -mr-2">
                              {tempStudentList.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center p-8 opacity-50 space-y-4">
                                  <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-2xl flex items-center justify-center text-2xl grayscale">
                                    📊
                                  </div>
                                  <p className="text-sm font-bold text-gray-400">Preview will appear here</p>
                                  <p className="text-xs text-gray-400 max-w-[200px]">Upload an Excel file to see the list of students before creating the college.</p>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  {tempStudentList.map((s, i) => (
                                    <div key={i} className="bg-white dark:bg-gray-900 p-4 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center justify-between group hover:border-indigo-200 dark:hover:border-indigo-900 transition-colors">
                                      <div className="flex gap-4 items-center overflow-hidden">
                                        <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex flex-shrink-0 items-center justify-center text-xs font-black">
                                          {i + 1}
                                        </div>
                                        <div className="min-w-0">
                                          <h5 className="font-bold text-sm text-gray-900 dark:text-white truncate">{s.name || 'Unnamed'}</h5>
                                          <div className="flex gap-2">
                                            <p className="text-[10px] font-mono text-gray-400">{s.id}</p>
                                            {s.department && <span className="text-[10px] font-bold bg-gray-100 dark:bg-gray-800 text-gray-500 px-1.5 py-0.5 rounded">{s.department}</span>}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : editingCollege ? (
                  <div className="flex flex-col h-full overflow-hidden">
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-4 bg-white dark:bg-gray-900 sticky top-0 z-20">
                      <div className="flex items-center gap-3">
                        <button onClick={() => { setEditingCollege(null); setOriginalEditingId(null); }} className="p-2 rounded-xl text-gray-500 hover:bg-white dark:hover:bg-gray-800 shadow-sm transition-all">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">{editingCollege.name}</h3>
                      </div>

                    </div>

                    <div className="flex-1 overflow-y-auto p-6 bg-white dark:bg-gray-900">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-full">
                        {/* LEFT CONFIG COLUMN */}
                        <div className="lg:col-span-1 space-y-6 flex flex-col h-full">
                          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm h-full flex flex-col">
                            <h4 className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-6 transition-colors">Configuration</h4>
                            <div className="space-y-6 flex-1">
                              <div>
                                <label className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase mb-2">Institution Name <span className="text-red-500">*</span></label>
                                <input
                                  type="text"
                                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border-2 dark:border-gray-700 rounded-xl font-bold text-sm text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                                  value={editingCollege.name}
                                  onChange={(e) => setEditingCollege({ ...editingCollege, name: e.target.value })}
                                />

                              </div>
                              <div>
                                <label className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase mb-2">College ID <span className="text-red-500">*</span></label>
                                <input
                                  type="text"
                                  disabled={!!editingCollege}
                                  className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 outline-none focus:border-indigo-500 transition-all ${editingCollege ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  placeholder="e.g. cec_2024"
                                  value={editingCollege ? editingCollege.id : newCollegeId}
                                  onChange={(e) => editingCollege ? setEditingCollege({ ...editingCollege, id: e.target.value }) : setNewCollegeId(e.target.value)}
                                />
                                {editingCollege && <p className="text-[10px] text-amber-600 dark:text-amber-500 font-bold mt-1 ml-1">College ID is permanent to maintain link and report integrity.</p>}
                                <p className="text-[10px] text-gray-400 mt-2">Unique identifier for this institution.</p>
                              </div>
                            </div>
                            <button
                              onClick={handleEditCollegeSave}
                              className="w-full bg-indigo-600 text-white py-4 rounded-xl font-bold shadow-lg shadow-indigo-100 dark:shadow-none hover:bg-indigo-700 transition-all mt-8"
                            >
                              Save Changes
                            </button>
                          </div>
                        </div>

                        {/* RIGHT LIST COLUMN */}
                        <div className="lg:col-span-2 space-y-4 flex flex-col h-full overflow-hidden">
                          <div className="flex items-center justify-between pb-2">
                            <h4 className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest transition-colors">Student List ({editingCollege.allowedStudents?.length || 0})</h4>

                            <div className="flex items-center gap-2">
                              {/* Hidden File Input */}
                              <input
                                type="file"
                                accept=".xlsx, .csv"
                                className="hidden"
                                ref={fileInputRef}
                                onChange={(e) => handleStudentListImport(e, true)}
                              />

                              {/* Import Button */}
                              <button
                                onClick={() => fileInputRef.current?.click()}
                                className="bg-green-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] shadow-lg shadow-green-100 dark:shadow-none hover:bg-green-700 transition-all flex items-center gap-1.5"
                              >
                                <span>📂</span> Import
                              </button>

                              {/* Export Button */}
                              <button
                                onClick={() => handleExportCollegeStudents(editingCollege)}
                                className="bg-green-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] shadow-lg shadow-green-100 dark:shadow-none hover:bg-green-700 transition-all flex items-center gap-1.5"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                Export
                              </button>

                              {/* Add Student Button */}
                              <button
                                onClick={() => {
                                  if (editingStudentId) return alert("Save current student before adding another.");
                                  const tempId = 'NEW_TEMP_' + Date.now();
                                  const newStudent = { id: tempId, name: '', department: '' };
                                  setEditingCollege({
                                    ...editingCollege,
                                    allowedStudents: [newStudent, ...(editingCollege.allowedStudents || [])]
                                  });
                                  setEditingStudentId(tempId);
                                  setTempStudent(newStudent);
                                  setStudentSearch('');
                                  studentListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                              >
                                + Add Student
                              </button>
                            </div>
                          </div>

                          <div className="px-1 pb-2">
                            <input
                              type="text"
                              placeholder="Search students by Name or ID..."
                              className="w-full px-4 py-2 bg-white dark:bg-gray-800 border-2 dark:border-gray-700 rounded-xl text-xs font-medium text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                              value={studentSearch}
                              onChange={(e) => setStudentSearch(e.target.value)}
                            />
                          </div>

                          <div ref={studentListRef} className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-3 pb-20">
                            {editingCollege.allowedStudents?.length === 0 && (
                              <div className="p-12 text-center bg-white dark:bg-gray-800 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700">
                                <p className="text-gray-400 font-bold text-sm">No students found.</p>
                                <p className="text-[10px] text-gray-400 mt-1">Import an Excel sheet to populate the list.</p>
                              </div>
                            )}
                            {editingCollege.allowedStudents?.filter(s =>
                              (s.name || '').toLowerCase().includes(studentSearch.toLowerCase()) ||
                              (s.id || '').toLowerCase().includes(studentSearch.toLowerCase())
                            ).map((s, i) => (
                              <div key={i} className={`p-4 bg-white dark:bg-gray-800 border-l-4 ${editingStudentId === s.id ? 'border-l-indigo-500 ring-2 ring-indigo-500/20' : 'border-l-green-400 border-y border-r border-gray-100 dark:border-gray-700'} rounded-xl transition-all shadow-sm hover:shadow-md group`}>
                                {editingStudentId === s.id && tempStudent ? (
                                  <div className="flex flex-col gap-3">
                                    <div className="grid grid-cols-12 gap-4">
                                      <div className="col-span-3">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase">Student ID</label>
                                        <input
                                          className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border-2 dark:border-gray-700 rounded-lg text-sm font-mono text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                                          value={tempStudent.id}
                                          onChange={e => setTempStudent({ ...tempStudent, id: e.target.value })}
                                        />
                                      </div>
                                      <div className="col-span-5">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase">Student Name</label>
                                        <input
                                          className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border-2 dark:border-gray-700 rounded-lg text-sm font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                                          value={tempStudent.name || ''}
                                          onChange={e => setTempStudent({ ...tempStudent, name: e.target.value })}
                                        />
                                      </div>
                                      <div className="col-span-4">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase">Dept</label>
                                        <input
                                          className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border-2 dark:border-gray-700 rounded-lg text-sm font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                                          value={tempStudent.department || ''}
                                          onChange={e => setTempStudent({ ...tempStudent, department: e.target.value })}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex gap-2 justify-end">
                                      <button
                                        onClick={() => {
                                          if (s.id.startsWith('NEW_TEMP_')) {
                                            const newList = (editingCollege.allowedStudents || []).filter(x => x.id !== s.id);
                                            setEditingCollege({ ...editingCollege, allowedStudents: newList });
                                          }
                                          setEditingStudentId(null);
                                          setTempStudent(null);
                                        }}
                                        className="px-4 py-1.5 text-xs font-bold text-gray-500 hover:bg-gray-100 rounded-lg"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={async () => {
                                          if (!tempStudent) return;
                                          const pid = tempStudent.id.trim();
                                          if (!pid) return alert("Student ID is required");

                                          // Check for duplicates (excluding current student being edited)
                                          const isDup = editingCollege.allowedStudents?.some(x => x.id === pid && x.id !== s.id);
                                          if (isDup) return alert("Duplicate Student ID found");

                                          const newList = (editingCollege.allowedStudents || []).map(original => original.id === s.id ? tempStudent : original);
                                          const updatedCollege = { ...editingCollege, allowedStudents: newList };

                                          setEditingCollege(updatedCollege);
                                          setEditingStudentId(null);
                                          setTempStudent(null);

                                          // Direct Save to Backend
                                          try {
                                            const collegeToSave = { ...updatedCollege };
                                            if (originalEditingId && originalEditingId !== collegeToSave.id) {
                                              if (colleges.find(c => c.id === collegeToSave.id)) {
                                                alert("Cannot save: New College ID already in use");
                                                return;
                                              }
                                              await StorageService.saveCollege(collegeToSave);
                                              await StorageService.deleteCollege(originalEditingId);
                                              setOriginalEditingId(collegeToSave.id);
                                            } else {
                                              await StorageService.saveCollege(collegeToSave);
                                            }
                                            await refreshData(true);
                                          } catch (err) {
                                            console.error("Auto-save failed", err);
                                            alert("Failed to save changes automatically.");
                                          }
                                        }}
                                        className="px-4 py-1.5 text-xs font-bold bg-green-600 text-white rounded-lg shadow-sm hover:bg-green-700"
                                      >
                                        Save
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-between cursor-pointer" onClick={() => { setEditingStudentId(s.id); setTempStudent(s); }}>
                                    <div>
                                      <h5 className="font-bold text-base text-gray-900 dark:text-white">{s.name || 'Unknown Student'}</h5>
                                      <div className="flex gap-2 items-center">
                                        <p className="text-xs font-mono text-gray-400 mt-0.5">{s.id}</p>
                                        {s.department && <span className="text-[10px] font-bold bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full">{s.department}</span>}
                                      </div>
                                    </div>
                                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (window.confirm('Remove this student?')) {
                                            const newList = (editingCollege.allowedStudents || []).filter(x => x.id !== s.id);
                                            const updatedCollege = { ...editingCollege, allowedStudents: newList };
                                            setEditingCollege(updatedCollege);

                                            // Direct Save for Delete
                                            if (originalEditingId && originalEditingId !== updatedCollege.id) {
                                              await StorageService.saveCollege(updatedCollege);
                                              await StorageService.deleteCollege(originalEditingId);
                                              setOriginalEditingId(updatedCollege.id);
                                            } else {
                                              await StorageService.saveCollege(updatedCollege);
                                            }
                                            await refreshData(true);
                                          }
                                        }}
                                        className="text-red-500 hover:bg-red-50 p-2 rounded-lg text-xs font-bold"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col h-full overflow-hidden">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 pb-10">
                      {colleges.map(c => (
                        <div key={c.id} className="p-5 border-2 border-gray-50 dark:border-gray-800 rounded-2xl bg-gray-50/30 dark:bg-gray-800/30 flex flex-col md:flex-row flex-wrap items-start md:items-center justify-between gap-4 transition-all hover:bg-white dark:hover:bg-gray-800 hover:border-indigo-100 dark:hover:border-indigo-900">
                          <div className="flex gap-4 items-center flex-1 min-w-[240px]">
                            <div className={`w-12 h-12 flex-shrink-0 rounded-2xl flex items-center justify-center font-black transition-colors text-[11px] ${c.isEnabled !== false ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-500' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}`}>
                              {c.isEnabled !== false ? 'ON' : 'OFF'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-xl font-black text-gray-900 dark:text-white break-words">{c.name}</h4>
                              <div className="flex flex-wrap gap-2 mt-1">
                                <span className="text-[10px] font-bold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full font-mono whitespace-nowrap">ID: {c.id}</span>
                                <span className="text-[10px] font-bold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 px-2 py-0.5 rounded-full whitespace-nowrap">👥 {c.allowedStudents?.length || 0} Students</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 flex-shrink-0">
                            <button
                              onClick={async () => { await StorageService.saveCollege({ ...c, isEnabled: c.isEnabled === false }); refreshData(true); }}
                              className={`px-4 py-2 rounded-lg font-bold text-xs transition-colors ${c.isEnabled !== false ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40' : 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40'}`}
                            >
                              {c.isEnabled !== false ? 'Disable' : 'Enable'}
                            </button>
                            <button
                              onClick={() => { setEditingCollege(c); setOriginalEditingId(c.id); }}
                              className="bg-indigo-600 text-white px-5 py-2 rounded-lg font-bold hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 dark:shadow-none text-xs"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteCollege(c.id)}
                              className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg font-bold hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors text-xs"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'assessments' && !editingAssessment && !showCreateAssessment && (
            <div className="flex flex-col h-full overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 transition-colors shadow-sm">
                <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white transition-colors">Assessments</h3>
                <button
                  onClick={() => setShowCreateAssessment(true)}
                  className="w-full sm:w-auto bg-indigo-600 text-white px-6 py-2 rounded-xl font-bold hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 dark:shadow-none text-sm"
                >
                  + Create New Assessment
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 pb-10">
                  {assessments.map(a => (
                    <div key={a.id} className="p-5 border-2 border-gray-50 dark:border-gray-800 rounded-2xl bg-gray-50/30 dark:bg-gray-800/30 flex flex-col md:flex-row flex-wrap items-start md:items-center justify-between gap-4 transition-all hover:bg-white dark:hover:bg-gray-800 hover:border-indigo-100 dark:hover:border-indigo-900">
                      <div className="flex gap-4 items-center flex-1 min-w-[240px]">
                        <div className={`w-12 h-12 flex-shrink-0 rounded-2xl flex items-center justify-center font-black transition-colors text-[11px] ${a.isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-500' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}`}>
                          {a.isActive ? 'ON' : 'OFF'}
                        </div>
                        <div>
                          <h4 className="text-xl font-black text-gray-900 dark:text-white break-words">{a.name}</h4>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <span className="text-[10px] font-bold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 px-2 py-0.5 rounded-full whitespace-nowrap">{a.duration} Mins</span>
                            <span className="text-[10px] font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-500 px-2 py-0.5 rounded-full whitespace-nowrap">Limit: {a.maxViolations} Violations</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 flex-shrink-0">
                        <button
                          onClick={async () => { await StorageService.saveAssessment({ ...a, isActive: !a.isActive }); refreshData(true); }}
                          className={`px-4 py-2 rounded-lg font-bold text-xs transition-colors ${a.isActive ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40' : 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40'}`}
                        >
                          {a.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleEditAssessment(a)}
                          className="bg-indigo-600 text-white px-5 py-2 rounded-lg font-bold hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 dark:shadow-none text-xs"
                        >
                          Configure
                        </button>
                        <button
                          onClick={() => handleDeleteAssessment(a.id)}
                          className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg font-bold hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors text-xs"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'assessments' && showCreateAssessment && (
            <div className="flex flex-col h-full overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 z-10 shadow-sm">
                <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white transition-colors">New Assessment</h3>
              </div>
              <div className="flex-1 overflow-hidden bg-white dark:bg-gray-900 flex flex-col">
                <div className="flex-1 overflow-hidden p-4 md:p-6">
                  <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
                    {/* LEFT: Form Config */}
                    <div className="flex flex-col h-full overflow-hidden">
                      <form id="create-assessment-form" onSubmit={handleCreateAssessment} className="contents">
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col h-full overflow-y-auto custom-scrollbar">
                          <h4 className="text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-4 flex-shrink-0">Assessment Details</h4>

                          <div className="space-y-4 flex-1">
                            <div>
                              <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-1.5">Assessment Name <span className="text-red-500">*</span></label>
                              <input
                                type="text"
                                className={`w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border-2 ${createErrors.name ? 'border-red-500' : 'dark:border-gray-700'} rounded-xl text-sm font-bold text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 outline-none focus:border-indigo-500 transition-all`}
                                placeholder="e.g. Software Engineer 2024"
                                value={newAsm.name}
                                onChange={e => { setNewAsm({ ...newAsm, name: e.target.value }); setCreateErrors({ ...createErrors, name: '' }); }}
                              />
                              {createErrors.name && <p className="text-xs text-red-500 font-bold mt-1 ml-1">{createErrors.name}</p>}
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-1.5">Resume PIN <span className="text-red-500">*</span></label>
                                <input
                                  type="text"
                                  className={`w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border-2 ${createErrors.resumePin ? 'border-red-500' : 'dark:border-gray-700'} rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all`}
                                  value={newAsm.resumePin}
                                  onChange={e => { setNewAsm({ ...newAsm, resumePin: e.target.value }); setCreateErrors({ ...createErrors, resumePin: '' }); }}
                                />
                                {createErrors.resumePin && <p className="text-xs text-red-500 font-bold mt-1 ml-1">{createErrors.resumePin}</p>}
                              </div>
                              <div>
                                <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-1.5">Duration (Min) <span className="text-red-500">*</span></label>
                                <input
                                  type="number"
                                  className={`w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border-2 ${createErrors.duration ? 'border-red-500' : 'dark:border-gray-700'} rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all`}
                                  value={newAsm.duration}
                                  onChange={e => { setNewAsm({ ...newAsm, duration: Number(e.target.value) }); setCreateErrors({ ...createErrors, duration: '' }); }}
                                />
                                {createErrors.duration && <p className="text-xs text-red-500 font-bold mt-1 ml-1">{createErrors.duration}</p>}
                              </div>
                              <div>
                                <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-1.5">Grace Period (Min)</label>
                                <input
                                  type="number"
                                  className={`w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border-2 dark:border-gray-700 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all`}
                                  value={newAsm.bufferTime ?? 20}
                                  onChange={e => { setNewAsm({ ...newAsm, bufferTime: Number(e.target.value) }); }}
                                  placeholder="20"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-1.5">Questions <span className="text-red-500">*</span></label>
                                <input
                                  type="number"
                                  className={`w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border-2 ${createErrors.questions ? 'border-red-500' : 'dark:border-gray-700'} rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all`}
                                  value={newAsm.questionCount}
                                  onChange={e => { setNewAsm({ ...newAsm, questionCount: Number(e.target.value) }); setCreateErrors({ ...createErrors, questions: '' }); }}
                                  max={tempUploadQuestions.length || 999}
                                />
                                {createErrors.questions && <p className="text-xs text-red-500 font-bold mt-1 ml-1">{createErrors.questions}</p>}
                                <p className="text-[10px] text-gray-400 mt-1">Total Loaded: {tempUploadQuestions.length}</p>
                              </div>
                              <div>
                                <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-1.5">Max Violations <span className="text-red-500">*</span></label>
                                <input
                                  type="number"
                                  className={`w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border-2 ${createErrors.maxViolations ? 'border-red-500' : 'dark:border-gray-700'} rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all`}
                                  value={newAsm.maxViolations}
                                  onChange={e => { setNewAsm({ ...newAsm, maxViolations: Number(e.target.value) }); setCreateErrors({ ...createErrors, maxViolations: '' }); }}
                                />
                                {createErrors.maxViolations && <p className="text-xs text-red-500 font-bold mt-1 ml-1">{createErrors.maxViolations}</p>}
                              </div>
                            </div>

                            <div>
                              <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-1.5">Question Bank (Excel/CSV) <span className="text-red-500">*</span></label>
                              <div className={`border-2 border-dashed ${createErrors.file ? 'border-red-500 bg-red-50/10' : 'border-gray-200 dark:border-gray-700'} rounded-xl p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group cursor-pointer relative flex items-center justify-center`}>
                                <input
                                  type="file"
                                  accept=".xlsx, .csv"
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                  onChange={handleNewAssessmentFileImport}
                                />
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg flex items-center justify-center text-lg group-hover:scale-110 transition-transform">
                                    📝
                                  </div>
                                  <div className="text-left">
                                    <div>
                                      <span className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline text-xs">Click to upload</span>
                                      <span className="text-gray-400 text-[10px] font-medium"> or drag & drop</span>
                                    </div>
                                    <p className="text-[9px] text-gray-400 font-mono">Supports .xlsx or .csv</p>
                                  </div>
                                </div>
                              </div>
                              {createErrors.file && <p className="text-xs text-red-500 font-bold mt-1 ml-1">{createErrors.file}</p>}
                              <div className="flex justify-between items-center mt-2">
                                <p className="text-[10px] font-bold text-gray-400">{tempUploadQuestions.length > 0 ? `✅ ${tempUploadQuestions.length} questions loaded` : 'No file selected'}</p>
                                <button type="button" onClick={downloadQuestionTemplate} className="text-[10px] font-bold text-indigo-500 hover:text-indigo-600 flex items-center gap-1 py-1 px-2 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors">
                                  ⬇️ Download Sample Template
                                </button>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl border-2 dark:border-gray-700">
                              <input
                                type="checkbox"
                                id="createRequireCamera"
                                className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                                checked={newAsm.requireCamera || false}
                                onChange={(e) => setNewAsm({ ...newAsm, requireCamera: e.target.checked })}
                              />
                              <label htmlFor="createRequireCamera" className="text-xs font-bold text-gray-700 dark:text-gray-300 select-none cursor-pointer">
                                Require Candidate Camera (Proctoring)
                              </label>
                            </div>

                            <div className="flex gap-3 pt-4 mt-6 border-t border-gray-100 dark:border-gray-700 flex-shrink-0">
                              <button type="button" onClick={() => setShowCreateAssessment(false)} className="flex-1 px-4 py-3 font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all text-sm">Cancel</button>
                              <button
                                type="submit"
                                className="flex-[2] bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold shadow-xl shadow-indigo-100 dark:shadow-none hover:bg-indigo-700 transition-all text-sm"
                              >
                                Create Assessment
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>

                    {/* RIGHT: Preview */}
                    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-4 md:p-6 border border-gray-100 dark:border-gray-800 overflow-hidden">
                      <div className="flex items-center justify-between mb-4 flex-shrink-0">
                        <h4 className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                          Question Preview {tempUploadQuestions.length > 0 && <span className="text-indigo-600 dark:text-indigo-400">({tempUploadQuestions.length})</span>}
                        </h4>
                        {tempUploadQuestions.length > 0 && (
                          <button type="button" onClick={() => setTempUploadQuestions([])} className="text-[10px] font-bold text-red-500 hover:bg-red-50 px-2 py-1 rounded">Clear List</button>
                        )}
                      </div>

                      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar -mr-2">
                        {tempUploadQuestions.length === 0 ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-8 opacity-50 space-y-4">
                            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-2xl flex items-center justify-center text-2xl grayscale">
                              📄
                            </div>
                            <p className="text-sm font-bold text-gray-400">Preview will appear here</p>
                            <p className="text-xs text-gray-400 max-w-[200px]">Upload questions to verify marks, options, and correct answers.</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {tempUploadQuestions.slice(0, 100).map((q, i) => (
                              <div key={i} className="bg-white dark:bg-gray-900 p-4 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm group hover:border-indigo-200 dark:hover:border-indigo-900 transition-colors">
                                <div className="flex gap-4 items-start">
                                  <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex flex-shrink-0 items-center justify-center text-[10px] font-black mt-1">
                                    Q{i + 1}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <h5 className="font-bold text-xs text-gray-900 dark:text-white mb-2 leading-relaxed">{q.text || 'Untitled Question'}</h5>

                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                      {Object.entries(q.options).map(([key, val]) => (
                                        <div key={key} className={`flex items-center gap-1.5 px-2 py-1 rounded border ${key === q.correctOption ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400' : 'bg-gray-50 dark:bg-gray-800 border-gray-100 dark:border-gray-700 text-gray-500'}`}>
                                          <span className="text-[9px] font-bold uppercase w-3">{key}</span>
                                          <span className="text-[10px] truncate">{val}</span>
                                        </div>
                                      ))}
                                    </div>

                                    <div className="flex gap-2 border-t dark:border-gray-800 pt-2">
                                      <span className="text-[9px] font-bold bg-gray-100 dark:bg-gray-800 text-gray-500 px-1.5 py-0.5 rounded uppercase">{q.category || 'General'}</span>
                                      <span className="text-[9px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-500 px-1.5 py-0.5 rounded">{q.marks} Marks</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {tempUploadQuestions.length > 100 && (
                              <p className="text-center text-[10px] text-gray-400 py-4 font-bold uppercase">... and {tempUploadQuestions.length - 100} more questions</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'assessments' && editingAssessment && (
            <div className="flex flex-col h-full overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 transition-colors bg-white dark:bg-gray-900 z-10 shadow-sm">
                <div className="flex items-center gap-3">
                  <button onClick={() => setEditingAssessment(null)} className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-all">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white transition-colors">{editingAssessment.name}</h3>
                </div>

              </div>

              <div className="flex-1 overflow-hidden p-5 dark:bg-gray-900 flex flex-col">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 h-full">
                  {/* Configuration Form */}
                  <div className="space-y-4 overflow-y-auto h-full pr-2 pb-20 custom-scrollbar">
                    <h4 className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest border-b dark:border-gray-800 pb-2 transition-colors">Configuration</h4>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase mb-2">Assessment Name <span className="text-red-500">*</span></label>
                      <input type="text" className={`w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 ${editErrors.name ? 'border-red-500' : 'dark:border-gray-700'} rounded-xl font-bold text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 outline-none focus:border-indigo-500 transition-all`} value={editingAssessment.name} onChange={e => { setEditingAssessment({ ...editingAssessment, name: e.target.value }); setEditErrors({ ...editErrors, name: '' }); }} />
                      {editErrors.name && <p className="text-xs text-red-500 font-bold mt-1 ml-1">{editErrors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase mb-2">Resume PIN <span className="text-red-500">*</span></label>
                      <input type="text" className={`w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 ${editErrors.resumePin ? 'border-red-500' : 'dark:border-gray-700'} rounded-xl font-bold text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 outline-none focus:border-indigo-500 transition-all`} value={editingAssessment.resumePin} onChange={e => { setEditingAssessment({ ...editingAssessment, resumePin: e.target.value }); setEditErrors({ ...editErrors, resumePin: '' }); }} />
                      {editErrors.resumePin && <p className="text-xs text-red-500 font-bold mt-1 ml-1">{editErrors.resumePin}</p>}
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase mb-2">Duration (Mins) <span className="text-red-500">*</span></label>
                      <input type="number" className={`w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 ${editErrors.duration ? 'border-red-500' : 'dark:border-gray-700'} rounded-xl text-gray-900 dark:text-white transition-all outline-none focus:border-indigo-500`} value={editingAssessment.duration} onChange={e => { setEditingAssessment({ ...editingAssessment, duration: Number(e.target.value) }); setEditErrors({ ...editErrors, duration: '' }); }} />
                      {editErrors.duration && <p className="text-xs text-red-500 font-bold mt-1 ml-1">{editErrors.duration}</p>}
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase mb-2">Grace Period (Min)</label>
                      <input type="number" className={`w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white transition-all outline-none focus:border-indigo-500`} value={editingAssessment.bufferTime ?? 20} onChange={e => { setEditingAssessment({ ...editingAssessment, bufferTime: Number(e.target.value) }); }} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase mb-2">Total Questions to Ask <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        className={`w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 ${editErrors.questionCount ? 'border-red-500' : 'dark:border-gray-700'} rounded-xl text-gray-900 dark:text-white transition-all outline-none focus:border-indigo-500`}
                        value={editingAssessment.questionCount}
                        min={1}
                        max={editingQuestions.length}
                        onChange={e => {
                          let val = Number(e.target.value);
                          if (val > editingQuestions.length) val = editingQuestions.length;
                          setEditingAssessment({ ...editingAssessment, questionCount: val });
                          setEditErrors({ ...editErrors, questionCount: '' });
                        }}
                        onBlur={() => {
                          if ((editingAssessment.questionCount || 0) < 1) {
                            setEditingAssessment({ ...editingAssessment, questionCount: 1 });
                          }
                        }}
                      />
                      {editErrors.questionCount && <p className="text-xs text-red-500 font-bold mt-1 ml-1">{editErrors.questionCount}</p>}
                      <p className="text-[10px] text-gray-400 mt-1 font-mono">Max available: {editingQuestions.length}</p>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase mb-2">Max Violations <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        className={`w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 ${editErrors.maxViolations ? 'border-red-500' : 'dark:border-gray-700'} rounded-xl text-gray-900 dark:text-white transition-all outline-none focus:border-indigo-500`}
                        value={editingAssessment.maxViolations}
                        onChange={e => {
                          setEditingAssessment({ ...editingAssessment, maxViolations: Number(e.target.value) });
                          setEditErrors({ ...editErrors, maxViolations: '' });
                        }}
                      />
                      {editErrors.maxViolations && <p className="text-xs text-red-500 font-bold mt-1 ml-1">{editErrors.maxViolations}</p>}
                    </div>

                    <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border-2 dark:border-gray-700 mt-4">
                      <input
                        type="checkbox"
                        id="editRequireCamera"
                        className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500"
                        checked={editingAssessment.requireCamera || false}
                        onChange={(e) => setEditingAssessment({ ...editingAssessment, requireCamera: e.target.checked })}
                      />
                      <label htmlFor="editRequireCamera" className="text-xs font-bold text-gray-700 dark:text-gray-300 select-none cursor-pointer">
                        Require Webcam Verification
                      </label>
                    </div>

                    <button onClick={handleSaveEditedAssessment} className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold shadow-lg shadow-indigo-100 dark:shadow-none hover:bg-indigo-700 transition-all mt-4">Save Changes</button>
                  </div>

                  {/* Question View */}
                  <div className="space-y-4 flex flex-col h-full overflow-hidden">
                    <div className="mb-4 space-y-3">
                      <div className="flex items-center justify-between border-b dark:border-gray-800 pb-2">
                        <h4 className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest transition-colors">Question Bank ({editingQuestions.length})</h4>
                        <div className="flex gap-2">
                          {/* Hidden File Input */}
                          <input
                            type="file"
                            accept=".xlsx, .csv"
                            className="hidden"
                            onChange={handleExcelImport}
                          />
                          <label className="bg-green-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] shadow-lg shadow-green-100 dark:shadow-none hover:bg-green-700 transition-all flex items-center gap-1.5 cursor-pointer">
                            <span>📂</span> Import
                            <input
                              type="file"
                              accept=".xlsx"
                              className="hidden"
                              onChange={handleExcelImport}
                            />
                          </label>

                          {/* Export Button */}
                          <button
                            onClick={handleExportQuestions}
                            className="bg-green-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] shadow-lg shadow-green-100 dark:shadow-none hover:bg-green-700 transition-all flex items-center gap-1.5"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            Export
                          </button>

                          {/* Add Question Button */}
                          <button
                            onClick={() => {
                              if (editingQuestionId) return alert("Save current question before adding another.");
                              const newQ: Question = {
                                id: `q_new_${Date.now()}`,
                                assessmentId: editingAssessment.id,
                                text: '',
                                options: { A: '', B: '', C: '', D: '' },
                                correctOption: 'A',
                                marks: 1
                              };
                              // Do not save immediately to avoid empty DB rows
                              setEditingQuestions([newQ, ...editingQuestions]);
                              setEditingQuestionId(newQ.id);
                              setTempQuestion(newQ);
                              setQuestionSearch(''); // Clear search to show new item
                              setCategoryFilter(''); // Clear filter to show new item
                              questionListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                          >
                            + Add Manual Question
                          </button>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Search questions..."
                          className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 dark:border-gray-700 rounded-xl text-xs font-medium text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                          value={questionSearch}
                          onChange={(e) => setQuestionSearch(e.target.value)}
                        />
                        <select
                          className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-2 dark:border-gray-700 rounded-xl text-[10px] font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                          value={categoryFilter}
                          onChange={(e) => setCategoryFilter(e.target.value)}
                        >
                          <option value="">All Categories</option>
                          {Array.from(new Set(editingQuestions.map(q => q.category).filter(Boolean))).sort().map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div ref={questionListRef} className="space-y-3 flex-1 overflow-y-auto pr-2 pb-20 custom-scrollbar">
                      {editingQuestions
                        .filter(q => {
                          const matchesSearch = q.text.toLowerCase().includes(questionSearch.toLowerCase());
                          const matchesCategory = !categoryFilter || q.category === categoryFilter;
                          return matchesSearch && matchesCategory;
                        })
                        .map((q, i) => (
                          <div key={q.id} className={`p-4 border-2 rounded-2xl transition-all ${editingQuestionId === q.id ? 'border-indigo-500 bg-indigo-50/20' : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 hover:border-indigo-200 dark:hover:border-indigo-900'}`}>
                            {editingQuestionId === q.id && tempQuestion ? (
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Question Text</label>
                                  <textarea
                                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border-2 dark:border-gray-700 rounded-xl text-sm font-medium text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                                    rows={2}
                                    value={tempQuestion.text}
                                    onChange={e => setTempQuestion({ ...tempQuestion, text: e.target.value })}
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  {(['A', 'B', 'C', 'D'] as const).map(opt => (
                                    <div key={opt}>
                                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Option {opt}</label>
                                      <input
                                        type="text"
                                        className="w-full px-3 py-2 bg-white dark:bg-gray-900 border-2 dark:border-gray-700 rounded-xl text-xs font-medium text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                                        value={tempQuestion.options[opt]}
                                        onChange={e => setTempQuestion({ ...tempQuestion, options: { ...tempQuestion.options, [opt]: e.target.value } })}
                                      />
                                    </div>
                                  ))}
                                </div>
                                <div className="flex gap-3">
                                  <div className="flex-1">
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Correct Option</label>
                                    <select
                                      className="w-full px-3 py-2 bg-white dark:bg-gray-900 border-2 dark:border-gray-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                                      value={tempQuestion.correctOption}
                                      onChange={e => setTempQuestion({ ...tempQuestion, correctOption: e.target.value as any })}
                                    >
                                      {['A', 'B', 'C', 'D'].map(o => <option key={o} value={o}>Option {o}</option>)}
                                    </select>
                                  </div>
                                  <div className="flex-1">
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Marks</label>
                                    <input
                                      type="number"
                                      className="w-full px-3 py-2 bg-white dark:bg-gray-900 border-2 dark:border-gray-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                                      value={tempQuestion.marks}
                                      onChange={e => setTempQuestion({ ...tempQuestion, marks: Number(e.target.value) })}
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Category (Aptitude, Technical, etc.)</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border-2 dark:border-gray-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition-all"
                                    placeholder="e.g. Aptitude"
                                    value={tempQuestion.category || ''}
                                    onChange={e => setTempQuestion({ ...tempQuestion, category: e.target.value })}
                                  />
                                </div>
                                <div className="flex gap-2 pt-2">
                                  <button
                                    onClick={() => {
                                      if (editingQuestionId?.startsWith('q_new_')) {
                                        setEditingQuestions(editingQuestions.filter(q => q.id !== editingQuestionId));
                                      }
                                      setEditingQuestionId(null);
                                      setTempQuestion(null);
                                    }}
                                    className="flex-1 py-2 rounded-xl font-bold text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (!tempQuestion.text.trim() || !tempQuestion.options.A.trim() || !tempQuestion.options.B.trim()) {
                                        alert("Please fill in the Question Text and at least Options A and B.");
                                        return;
                                      }

                                      // setIsLoading(true); // Silent save
                                      await StorageService.saveQuestion(tempQuestion);

                                      setEditingQuestions(editingQuestions.map(qx => qx.id === tempQuestion.id ? tempQuestion : qx));
                                      setEditingQuestionId(null);
                                      setTempQuestion(null);
                                      // setIsLoading(false);
                                    }}
                                    className="flex-1 py-2 bg-indigo-600 text-white rounded-xl font-bold text-xs shadow-md hover:bg-indigo-700 transition-colors"
                                  >
                                    Save Question
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="cursor-pointer group" onClick={() => { setEditingQuestionId(q.id); setTempQuestion({ ...q }); }}>

                                <div className="flex justify-between items-start mb-2">
                                  <h5 className="font-bold text-sm text-gray-900 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors flex-1 pr-4">
                                    <span className="text-gray-400 text-xs mr-2">#{i + 1}</span>
                                    {q.text}
                                  </h5>
                                  <div className="flex items-center gap-2">
                                    {q.category && (
                                      <span className="bg-indigo-100 dark:bg-indigo-900/40 text-[9px] font-black px-2 py-0.5 rounded text-indigo-600 dark:text-indigo-300 uppercase letter tracking-tighter">
                                        {q.category}
                                      </span>
                                    )}
                                    <span className="bg-gray-200 dark:bg-gray-700 text-[10px] font-bold px-2 py-0.5 rounded text-gray-600 dark:text-gray-300">
                                      {q.marks} Pts
                                    </span>
                                    {(() => {
                                      const relatedAttempts = attempts.filter(a => a.assessmentId === editingAssessment.id && a.responses?.[q.id]);
                                      const correctCount = relatedAttempts.filter(a => a.responses[q.id] === q.correctOption).length;
                                      const total = relatedAttempts.length;
                                      const rate = total > 0 ? Math.round((correctCount / total) * 100) : null;

                                      if (rate === null) return null;

                                      const colorClass = rate >= 70 ? 'text-green-500' : rate >= 40 ? 'text-orange-500' : 'text-red-500';

                                      return (
                                        <span className={`text-[10px] font-black px-2 py-0.5 bg-gray-50 dark:bg-gray-800 rounded border dark:border-gray-700 ${colorClass}`} title={`${correctCount}/${total} correct answers`}>
                                          {rate}% Accuracy
                                        </span>
                                      );
                                    })()}
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (window.confirm("Delete this question?")) {
                                          await StorageService.deleteQuestion(q.id);
                                          const newQuestions = editingQuestions.filter(x => x.id !== q.id);
                                          setEditingQuestions(newQuestions);

                                          // Auto-update assessment count if necessary
                                          if (editingAssessment.questionCount > newQuestions.length) {
                                            const newCount = newQuestions.length;
                                            const updatedAsm = { ...editingAssessment, questionCount: newCount };
                                            setEditingAssessment(updatedAsm);
                                            // Save immediately to persist the count fix
                                            await StorageService.saveAssessment(updatedAsm);
                                          }
                                        }
                                      }}
                                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                                  {(['A', 'B', 'C', 'D'] as const).map(opt => (
                                    <div key={opt} className={`text-xs px-2 py-1 rounded flex items-center gap-2 ${q.correctOption === opt ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 font-bold' : 'text-gray-500 dark:text-gray-400'}`}>
                                      <span className="uppercase opacity-50">{opt}.</span> {q.options[opt]}
                                    </div>
                                  ))}
                                </div>
                                <p className="text-[10px] text-center text-gray-300 dark:text-gray-600 font-bold uppercase tracking-widest mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  Tap to Edit
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'reports' && (
            <div className="flex flex-col h-full overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 z-10 shadow-sm space-y-3">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white transition-colors">Reports</h3>
                  <div className="flex gap-2">
                    {selectedReportIds.size > 0 && (
                      <button
                        onClick={() => {
                          setConfirmDelete({
                            id: '',
                            ids: Array.from(selectedReportIds),
                            type: 'bulk_attempt',
                            title: 'Delete Selected Reports?',
                            description: `Are you sure you want to delete ${selectedReportIds.size} selected reports? This cannot be undone.`
                          });
                        }}
                        className="bg-red-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-red-700 transition shadow-lg shadow-red-100 dark:shadow-none flex items-center gap-2 whitespace-nowrap text-xs sm:text-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        Delete ({selectedReportIds.size})
                      </button>
                    )}
                    <button
                      onClick={handleExportExcel}
                      className="bg-green-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-green-700 transition shadow-lg shadow-green-100 dark:shadow-none flex items-center gap-2 whitespace-nowrap text-xs sm:text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      Export Excel
                    </button>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row items-center gap-2 w-full">
                  <input
                    type="text"
                    placeholder="Search name or student ID..."
                    className="w-full md:flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 dark:border-gray-700 rounded-xl outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all font-medium text-sm"
                    value={reportFilter}
                    onChange={(e) => { setReportFilter(e.target.value); setCurrentPage(1); }}
                  />
                  <select
                    value={selectedCollege}
                    onChange={(e) => { setSelectedCollege(e.target.value); setCurrentPage(1); }}
                    className="w-full md:w-56 px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 dark:border-gray-700 rounded-xl outline-none text-gray-900 dark:text-white font-bold transition-all text-sm"
                  >
                    <option value="" className="bg-white dark:bg-gray-900">All Colleges</option>
                    {colleges.map(c => (
                      <option key={c.id} value={c.id} className="bg-white dark:bg-gray-900">{c.name}</option>
                    ))}
                  </select>
                  <select
                    value={assessmentFilter}
                    onChange={(e) => { setAssessmentFilter(e.target.value); setCurrentPage(1); }}
                    className="w-full md:w-56 px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 dark:border-gray-700 rounded-xl outline-none text-gray-900 dark:text-white font-bold transition-all text-sm"
                  >
                    <option value="" className="bg-white dark:bg-gray-900">All Assessments</option>
                    {assessments.map(a => (
                      <option key={a.id} value={a.id} className="bg-white dark:bg-gray-900">{a.name}</option>
                    ))}
                  </select>
                  <select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                    className="w-full md:w-48 px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 dark:border-gray-700 rounded-xl outline-none text-gray-900 dark:text-white font-bold transition-all text-sm"
                  >
                    <option value="" className="bg-white dark:bg-gray-900">All Status</option>
                    <option value="SUBMITTED" className="bg-white dark:bg-gray-900">Submitted</option>
                    <option value="LOCKED" className="bg-white dark:bg-gray-900">Locked</option>
                    <option value="ABANDONED" className="bg-white dark:bg-gray-900">Abandoned</option>
                    <option value="IN_PROGRESS" className="bg-white dark:bg-gray-900">In Progress</option>
                  </select>
                  <select
                    value={verdictFilter}
                    onChange={(e) => { setVerdictFilter(e.target.value); setCurrentPage(1); }}
                    className="w-full md:w-48 px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 dark:border-gray-700 rounded-xl outline-none text-gray-900 dark:text-white font-bold transition-all text-sm"
                  >
                    <option value="" className="bg-white dark:bg-gray-900">All Verdicts</option>
                    <option value="POTENTIAL" className="bg-white dark:bg-gray-900">Potential Hire</option>
                    <option value="REVIEW" className="bg-white dark:bg-gray-900">Needs Review</option>
                    <option value="RISK" className="bg-white dark:bg-gray-900">At Risk</option>
                    <option value="REJECT" className="bg-white dark:bg-gray-900">Low Score</option>
                  </select>
                </div>
              </div>

              <div className="flex-1 p-5 min-h-0 flex flex-col">
                <div className="flex-1 min-h-0 rounded-2xl border-2 border-gray-50 dark:border-gray-800 shadow-sm bg-white dark:bg-gray-900/50 flex flex-col overflow-hidden">
                  <div className="overflow-auto flex-1 custom-scrollbar">
                    <table className="w-full text-left border-collapse">
                      <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 shadow-sm">
                        <tr className="border-b border-gray-100 dark:border-gray-700">
                          <th className="px-4 py-5 text-left bg-gray-50 dark:bg-gray-800">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                              checked={paginatedAttempts.length > 0 && paginatedAttempts.every(a => selectedReportIds.has(a.id))}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th
                            className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors select-none"
                            onClick={() => toggleSort('candidateName')}
                          >
                            <div className="flex items-center gap-2">
                              Name
                              {sortConfig.key === 'candidateName' && (
                                <span className="text-indigo-600 dark:text-indigo-400">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </div>
                          </th>
                          <th
                            className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors select-none"
                            onClick={() => toggleSort('studentId')}
                          >
                            <div className="flex items-center gap-2">
                              Student ID
                              {sortConfig.key === 'studentId' && (
                                <span className="text-indigo-600 dark:text-indigo-400">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </div>
                          </th>
                          <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 text-left">College</th>
                          <th
                            className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors select-none text-left"
                            onClick={() => toggleSort('score')}
                          >
                            <div className="flex items-center gap-2">
                              Score
                              {sortConfig.key === 'score' && (
                                <span className="text-indigo-600 dark:text-indigo-400">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </div>
                          </th>
                          <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 text-left">Time Taken</th>
                          <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 text-left">Violations</th>
                          <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 text-left">Verdict</th>
                          <th className="px-8 py-5 text-[10px] font-black uppercase dark:text-gray-500 text-left">Status</th>
                          <th className="px-8 py-5 text-[10px] font-black uppercase dark:text-gray-500 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                        {paginatedAttempts.map(a => (
                          <tr key={a.id} className="group hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors">
                            <td className="px-4 py-4">
                              <input
                                type="checkbox"
                                className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                checked={selectedReportIds.has(a.id)}
                                onChange={() => handleSelectReport(a.id)}
                              />
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm font-bold text-gray-900 dark:text-white transition-colors">{a.candidateName}</p>
                              <p className="text-[9px] font-black text-indigo-500 dark:text-indigo-400 uppercase tracking-wider mb-0.5">
                                {assessments.find(as => as.id === a.assessmentId)?.name || 'Unknown Assessment'}
                              </p>
                              <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 flex items-center gap-1.5">
                                <span>{new Date(a.startTime).toLocaleDateString()}</span>
                                <span className="text-gray-300 dark:text-gray-700">|</span>
                                <span>{new Date(a.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 font-mono text-[11px] font-bold text-gray-500 dark:text-gray-400">{a.studentId}</td>
                            <td className="px-6 py-4 text-[11px] font-bold text-gray-600 dark:text-gray-300 w-64 leading-snug">{a.collegeName || colleges.find(c => c.id === a.collegeId)?.name || (a.collegeId ? `ID: ${a.collegeId}` : 'Unknown')}</td>
                            <td className="px-6 py-4 font-black text-sm text-indigo-600 dark:text-indigo-400">{a.score ?? 0}</td>
                            <td className="px-6 py-4 font-mono text-[11px] font-bold text-gray-500 dark:text-gray-400">
                              {a.endTime ? (
                                <span>{Math.floor((a.endTime - a.startTime) / 60000)}m {Math.floor(((a.endTime - a.startTime) % 60000) / 1000)}s</span>
                              ) : <span className="text-gray-300">-</span>}
                            </td>
                            <td className="px-6 py-4 font-bold text-sm text-red-500 dark:text-red-400/80">{a.violations}</td>
                            <td className="px-6 py-4">
                              {(() => {
                                const v = getVerdict(a);
                                return (
                                  <span className={`inline-flex items-center justify-center w-24 px-2 py-1 rounded-md text-[9px] font-black border uppercase tracking-tighter ${v.class}`}>
                                    {v.label}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <span className={`inline-flex items-center justify-center w-32 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest shadow-sm ${a.status === AttemptStatus.SUBMITTED ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-500 border border-green-200 dark:border-green-900/50' :
                                a.status === AttemptStatus.ABANDONED ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-500 border border-red-200 dark:border-red-900/50' :
                                  a.status === AttemptStatus.LOCKED ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-500 border border-amber-200 dark:border-amber-900/50' :
                                    'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-500 border border-blue-200 dark:border-blue-900/50'
                                }`}>
                                {a.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleViewReport(a)}
                                  disabled={loadingReportId !== null}
                                  className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-3 py-2 rounded-lg font-bold hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-all text-[11px] flex items-center justify-center min-w-[36px] h-9 disabled:opacity-50"
                                  title="View Report"
                                >
                                  {loadingReportId === a.id ? (
                                    <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                  )}
                                </button>
                                <button
                                  onClick={() => {
                                    setConfirmDelete({
                                      id: a.id,
                                      type: 'attempt',
                                      title: 'Delete Report?',
                                      description: `Permanently delete the attempt record for ${a.candidateName}? This cannot be undone.`
                                    });
                                  }}
                                  className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg font-bold hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors h-9 flex items-center justify-center"
                                  title="Delete Report"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  {/* Pagination Controls - Always Visible Footer */}
                  <div className="border-t border-gray-100 dark:border-gray-800 px-8 py-4 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 mt-auto">
                    <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                      Showing <span className="font-bold text-gray-900 dark:text-white">{sortedAttempts.length > 0 ? startIndex + 1 : 0}</span> to <span className="font-bold text-gray-900 dark:text-white">{Math.min(endIndex, sortedAttempts.length)}</span> of <span className="font-bold text-gray-900 dark:text-white">{sortedAttempts.length}</span> results
                    </div>

                    {totalPages > 1 && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="px-4 py-2 rounded-xl font-bold text-sm bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                          Previous
                        </button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`w-10 h-10 rounded-xl font-bold text-sm transition-all ${currentPage === page
                                ? 'bg-indigo-600 text-white shadow-lg'
                                : 'bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                            >
                              {page}
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="px-4 py-2 rounded-xl font-bold text-sm bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}



          {activeTab === 'links' && (
            <div className="flex flex-col h-full overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 z-10 shadow-sm">
                <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white transition-colors">Link Generation</h3>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                <div className="bg-gray-50 dark:bg-gray-800 p-5 rounded-[2rem] border border-gray-100 dark:border-gray-700 mb-6 transition-colors">
                  <h4 className="text-sm font-black text-gray-900 dark:text-white mb-4 transition-colors">Create New Access Link</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                    <div>
                      <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-2">Institution</label>
                      <select
                        value={linkCollegeId}
                        onChange={(e) => setLinkCollegeId(e.target.value)}
                        className="w-full px-4 py-2 bg-white dark:bg-gray-900 border-2 dark:border-gray-700 rounded-xl outline-none text-gray-900 dark:text-white font-bold appearance-none transition-all text-sm"
                      >
                        <option value="" className="dark:bg-gray-900">Select College...</option>
                        {colleges.filter(c => c.isEnabled).map(c => (
                          <option key={c.id} value={c.id} className="dark:bg-gray-900">{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-2">Assessment</label>
                      <select
                        value={linkAssessmentId}
                        onChange={(e) => setLinkAssessmentId(e.target.value)}
                        className="w-full px-4 py-2 bg-white dark:bg-gray-900 border-2 dark:border-gray-700 rounded-xl outline-none text-gray-900 dark:text-white font-bold appearance-none transition-all text-sm"
                      >
                        <option value="" className="dark:bg-gray-900">Select Assessment...</option>
                        {assessments.filter(a => a.isActive).map(a => (
                          <option key={a.id} value={a.id} className="dark:bg-gray-900">{a.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-1 md:col-span-2 lg:col-span-1">
                      <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase mb-2">Custom URL Slug</label>
                      <input
                        type="text"
                        placeholder="e.g. campus2024"
                        className="w-full px-4 py-2 bg-white dark:bg-gray-900 border-2 dark:border-gray-700 rounded-xl outline-none text-gray-900 dark:text-white font-bold placeholder-gray-300 dark:placeholder-gray-600 transition-all text-sm"
                        value={newLinkSlug}
                        onChange={(e) => setNewLinkSlug(e.target.value)}
                      />
                    </div>
                    <button
                      onClick={async () => {
                        if (!newLinkSlug || !linkCollegeId || !linkAssessmentId) {
                          alert("Please fill all fields");
                          return;
                        }

                        // Check for reserved keywords
                        const reservedWords = ['admin', 'recruiter-portal', 'dashboard', 'login', 'logout', 'settings', 'api', 'portal', 'hr-access'];
                        if (reservedWords.includes(newLinkSlug.toLowerCase())) {
                          alert(`"${newLinkSlug}" is a reserved keyword and cannot be used as a link name. Please choose a different name.`);
                          return;
                        }

                        try {
                          // setIsLoading(true); // Silent
                          await StorageService.createAccessLink(newLinkSlug, linkCollegeId, linkAssessmentId);
                          setNewLinkSlug('');
                          setLinkCollegeId('');
                          setLinkAssessmentId('');
                          await refreshData(true);
                        } catch (e: any) {
                          alert(e.message);
                          // setIsLoading(false);
                        }
                      }}
                      className="bg-indigo-600 text-white px-6 py-2 rounded-xl font-bold shadow-lg shadow-indigo-100 dark:shadow-none hover:bg-indigo-700 transition h-full text-sm"
                    >
                      Generate Link
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 pb-10">
                  {links.map(link => (
                    <div key={link.id} className="p-4 border-2 border-gray-50 dark:border-gray-800 bg-white dark:bg-gray-800/50 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4 group hover:border-indigo-100 dark:hover:border-indigo-900 transition-all">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest">LINK</span>
                          <h4 className="text-lg font-bold text-gray-900 dark:text-white transition-colors">/{link.id}</h4>
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 transition-colors">
                          Assigned to: <span className="font-bold text-gray-600 dark:text-gray-300">{colleges.find(c => c.id === link.collegeId)?.name}</span> •
                          Test: <span className="font-bold text-gray-600 dark:text-gray-300">{assessments.find(a => a.id === link.assessmentId)?.name}</span>
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/${link.id}`;
                            navigator.clipboard.writeText(url);
                            alert("Link copied to clipboard!");
                          }}
                          className="px-4 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-xl font-bold text-xs uppercase tracking-widest transition"
                        >
                          Copy URL
                        </button>
                        <button
                          onClick={() => {
                            setConfirmDelete({
                              id: link.id,
                              type: 'link',
                              title: 'Delete Link?',
                              description: 'Permanently delete this access link? Students will no longer be able to use it.'
                            });
                          }}
                          className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg font-bold hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors text-xs"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  {links.length === 0 && <p className="col-span-full text-center text-gray-400 dark:text-gray-500 py-10 font-medium transition-colors">No active links found. Generate one above.</p>}
                </div>
              </div>
            </div>
          )}


          {activeTab === 'settings' && settings && (
            <div className="flex flex-col h-full overflow-hidden bg-white dark:bg-gray-950">
              {/* Header */}
              <div className="px-6 py-6 md:px-10 md:py-8 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 flex-shrink-0 z-10">
                <div className="flex flex-col gap-2">
                  <h3 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">Security Center</h3>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                      Access Control
                    </span>
                    <span className="hidden sm:block w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-700"></span>
                    <span className="flex items-center gap-1.5 text-indigo-600 dark:text-indigo-400 font-bold bg-indigo-50 dark:bg-indigo-900/20 px-2 py-0.5 rounded-md">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      Last Updated: {settings.updatedAt ? new Date(settings.updatedAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : 'Never'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Main Content - No Scroll */}
              <div className="flex-1 overflow-hidden p-6 md:p-10 flex flex-col justify-start">
                <div className="w-full max-w-5xl space-y-8 md:space-y-10">

                  {/* Recruiter PIN Section */}
                  <div className="space-y-6">
                    <div className="flex items-start gap-4 md:gap-6">
                      <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/10 rounded-2xl flex items-center justify-center flex-shrink-0 text-3xl shadow-sm border border-indigo-100 dark:border-indigo-900/30">
                        🔐
                      </div>
                      <div className="space-y-1.5">
                        <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">Recruiter Access PIN</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-2xl leading-relaxed">
                          This high-privilege PIN authorizes recruiters to unlock assessment terminals and access sensitive controls at college venues. Please ensure this is kept secure and updated regularly.
                        </p>
                      </div>
                    </div>

                    <div className="max-w-xl pl-0 sm:pl-[4.5rem] md:pl-[5.5rem] space-y-6 md:space-y-8">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">Set New PIN / Password</label>
                        <div className="relative group/input">
                          <input
                            type="text"
                            className="w-full px-6 py-4 bg-gray-50 hover:bg-white focus:bg-white dark:bg-gray-900 dark:hover:bg-gray-800 dark:focus:bg-gray-800 border-2 border-gray-100 dark:border-gray-800 focus:border-indigo-500 dark:focus:border-indigo-500 rounded-2xl text-left text-xl md:text-2xl font-bold text-gray-900 dark:text-white tracking-[0.2em] placeholder-gray-300 dark:placeholder-gray-700 transition-all outline-none font-mono shadow-sm"
                            placeholder="CODE"
                            value={newRecruitPin}
                            onChange={(e) => setNewRecruitPin(e.target.value)}
                          />
                          <div className="absolute inset-y-0 right-4 flex items-center gap-2 pointer-events-none">
                            <span className={`text-[10px] font-bold px-2.5 py-1 rounded-lg transition-colors border ${newRecruitPin.length >= 4 ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-100 dark:border-green-800' : 'bg-gray-100 dark:bg-gray-800 text-gray-400 border-transparent'}`}>
                              {newRecruitPin.length >= 4 ? 'Strong' : 'Min 4 Chars'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-start pt-2">
                        <button
                          onClick={async () => {
                            if (newRecruitPin.length < 4) return alert('PIN must be at least 4 characters');
                            await StorageService.updateSettings({
                              ...settingsForm,
                              recruitAccessPin: newRecruitPin,
                              updatedAt: Date.now()
                            });
                            setNewRecruitPin('');
                            alert('Recruiter Access PIN updated successfully.');
                            refreshData(true);
                          }}
                          className="w-full sm:w-auto bg-gray-900 dark:bg-indigo-600 text-white px-8 py-3.5 rounded-xl font-bold text-sm md:text-base shadow-xl shadow-gray-200 dark:shadow-none hover:bg-black dark:hover:bg-indigo-700 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2.5"
                        >
                          <span>Update Credentials</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="pt-auto mt-auto">
                    <div className="border-t border-gray-100 dark:border-gray-800 pt-6">
                      <p className="text-[10px] font-bold text-gray-400 dark:text-gray-600 uppercase tracking-widest flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-sm shadow-emerald-200 dark:shadow-none animate-pulse"></span>
                        Secure Environment • End-to-End Encrypted
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


          {selectedReport && (
            <div className="absolute inset-0 z-[100] bg-white dark:bg-gray-950 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-5 duration-300">
              {/* Header */}
              <div className="px-8 py-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-white dark:bg-gray-900 sticky top-0 shadow-sm">
                <div className="flex items-center gap-4">
                  <button onClick={() => setSelectedReport(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <div>
                    <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Hire Report: {selectedReport.candidateName}</h3>
                    <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 font-mono tracking-widest uppercase mt-0.5">ID: {selectedReport.studentId} • {selectedReport.collegeName || colleges.find(c => c.id === selectedReport.collegeId)?.name || (selectedReport.collegeId ? `ID: ${selectedReport.collegeId}` : 'Unknown')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border ${selectedReport.status === AttemptStatus.SUBMITTED ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-100 dark:border-green-900/30' : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-100 dark:border-amber-900/30'
                    }`}>
                    {selectedReport.status}
                  </div>
                  <button onClick={() => setSelectedReport(null)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-8 custom-scrollbar dark:bg-gray-950">
                <div className="max-w-6xl mx-auto space-y-8">

                  {/* Top Score Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {(() => {
                      const totalMax = reportQuestions.reduce((sum, q) => sum + q.marks, 0) || 1;
                      // Dynamic Score: Recalculate based on current question marks (handles updates/corrections)
                      const derivedScore = reportQuestions.reduce((sum, q) => {
                        return selectedReport.responses[q.id] === q.correctOption ? sum + q.marks : sum;
                      }, 0);

                      return (
                        <>
                          <div className="bg-indigo-600 p-6 rounded-3xl text-white shadow-xl shadow-indigo-100 dark:shadow-none transition-transform hover:scale-[1.02]">
                            <p className="text-indigo-100 text-[10px] font-black uppercase tracking-widest opacity-70">Total Score</p>
                            <div className="flex items-baseline gap-2 mt-2">
                              <h3 className="text-4xl font-black">{derivedScore}</h3>
                              <span className="text-indigo-200 text-sm font-bold">/ {totalMax} <span className="text-[10px]">pts</span></span>
                            </div>
                          </div>
                          <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl border-2 border-gray-50 dark:border-gray-700 shadow-sm transition-transform hover:scale-[1.02]">
                            <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Accuracy %</p>
                            <h3 className="text-4xl font-black text-gray-900 dark:text-white mt-2">
                              {Math.round((derivedScore / (totalMax || 1)) * 100)}%
                            </h3>
                          </div>
                        </>
                      );
                    })()}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl border-2 border-gray-50 dark:border-gray-700 shadow-sm transition-transform hover:scale-[1.02]">
                      <p className="text-red-400 text-[10px] font-black uppercase tracking-widest">Violations</p>
                      <h3 className={`text-4xl font-black mt-2 ${selectedReport.violations > 0 ? 'text-red-500' : 'text-green-500'}`}>
                        {selectedReport.violations}
                      </h3>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl border-2 border-gray-50 dark:border-gray-700 shadow-sm transition-transform hover:scale-[1.02]">
                      <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Time Taken</p>
                      <h3 className="text-2xl font-black text-gray-900 dark:text-white mt-2">
                        {selectedReport.endTime ? (
                          <span>{Math.floor((selectedReport.endTime - selectedReport.startTime) / 60000)}m {Math.floor(((selectedReport.endTime - selectedReport.startTime) % 60000) / 1000)}s</span>
                        ) : 'DNF'}
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Hiring Recommendation & Categorized Analysis */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2 space-y-8">
                    {/* Recruiter Insights Summary */}
                    <div className="bg-gray-900 border-l-4 border-indigo-500 p-8 rounded-[2rem] text-white shadow-xl">
                      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="space-y-2 text-center md:text-left">
                          <h4 className="text-xs font-black uppercase tracking-[0.2em] text-indigo-400">Recruiter Verdict</h4>
                          {(() => {
                            const totalPossible = reportQuestions.reduce((sum, q) => sum + q.marks, 0);

                            // Dynamic Score for Verdict
                            const derivedScore = reportQuestions.reduce((sum, q) => {
                              return selectedReport.responses[q.id] === q.correctOption ? sum + q.marks : sum;
                            }, 0);

                            const scorePerc = (derivedScore / (totalPossible || 1)) * 100;

                            // Recalculate verdict based on ACCURATE points (not estimate)
                            let v = { label: 'REVIEW', class: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-500 border-amber-200' };
                            const hasHighViolations = selectedReport.violations >= 3;

                            if (scorePerc >= 75 && !hasHighViolations) v = { label: 'POTENTIAL', class: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-500 border-green-200' };
                            else if (hasHighViolations) v = { label: 'RISK', class: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-500 border-red-200' };
                            else if (scorePerc < 40) v = { label: 'REJECT', class: 'bg-red-50 dark:bg-gray-800 text-gray-700 dark:text-gray-400 border-gray-200' };

                            let reason = "Candidate performance is average. Manual review of detailed answers recommended.";
                            if (v.label === 'POTENTIAL') reason = "Outstanding performance and clean audit history. Highly recommended for follow-up.";
                            if (v.label === 'RISK') reason = "Critical violations recorded. Multiple security infractions detected during assessment.";
                            if (v.label === 'REJECT') reason = "Candidate did not meet the minimum competency threshold for this assessment.";

                            return (
                              <div className="space-y-4">
                                <div className={`inline-block ${v.class} px-6 py-2 rounded-2xl text-[14px] font-black uppercase tracking-[0.1em] shadow-lg`}>
                                  {v.label === 'POTENTIAL' ? '🔥 HIGH POTENTIAL' : v.label === 'RISK' ? '⚠️ RISK DETECTED' : v.label === 'REJECT' ? '❌ DO NOT HIRE' : '🔍 NEEDS REVIEW'}
                                </div>
                                <p className="text-sm font-medium leading-relaxed text-gray-400 italic">"{reason}"</p>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="hidden md:block w-px h-16 bg-gray-800"></div>
                        <div className="text-center md:text-right">
                          <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Final Score</p>
                          <h2 className="text-4xl font-black text-white">{selectedReport.score || 0} <span className="text-sm text-gray-500">pts</span></h2>
                        </div>
                      </div>
                    </div>

                    {/* Category Analysis */}
                    <div className="bg-white dark:bg-gray-900 p-8 rounded-[2rem] border-2 border-gray-50 dark:border-gray-800 shadow-sm">
                      <h4 className="text-lg font-black text-gray-900 dark:text-white mb-6 uppercase tracking-wider flex items-center gap-2">
                        <span className="w-2 h-6 bg-indigo-500 rounded-full"></span>
                        Performance by Category
                      </h4>
                      <div className="space-y-6">
                        {Array.from(new Set(reportQuestions.map(q => q.category || 'Uncategorized'))).map(cat => {
                          const catQuestions = reportQuestions.filter(q => (q.category || 'Uncategorized') === cat);
                          const catScore = catQuestions.reduce((sum, q) => {
                            return sum + (selectedReport.responses?.[q.id] === q.correctOption ? q.marks : 0);
                          }, 0);
                          const catTotal = catQuestions.reduce((sum, q) => sum + q.marks, 0);
                          const perc = Math.round((catScore / catTotal) * 100);

                          return (
                            <div key={cat} className="space-y-3">
                              <div className="flex justify-between items-end">
                                <span className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">{cat}</span>
                                <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 font-mono">{catScore} / {catTotal} pts ({perc}%)</span>
                              </div>
                              <div className="h-3 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden border dark:border-gray-700">
                                <div className={`h-full rounded-full transition-all duration-1000 ${perc >= 70 ? 'bg-green-500' : perc >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${perc}%` }}></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Audit Logs */}
                    <div className="bg-white dark:bg-gray-900 p-8 rounded-[2rem] border-2 border-gray-50 dark:border-gray-800 shadow-sm">
                      <h4 className="text-lg font-black text-gray-900 dark:text-white mb-6 uppercase tracking-wider">Candidate Audit History</h4>
                      <div className="space-y-4">
                        {selectedReport.auditLogs.map((log, i) => (
                          <div key={i} className="flex gap-4 items-start group">
                            <div className="text-[10px] font-mono font-bold text-gray-400 mt-1 min-w-[70px]">{new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
                            <div className="relative">
                              <div className={`w-3 h-3 rounded-full mt-1.5 z-10 relative ${log.type === 'TAB_SWITCH' ? 'bg-red-500 shadow-lg shadow-red-200' :
                                log.type === 'START' ? 'bg-green-500 shadow-lg shadow-green-200' :
                                  log.type === 'SUBMIT' ? 'bg-indigo-500 shadow-lg shadow-indigo-200' : 'bg-gray-300'
                                }`}></div>
                              {i !== selectedReport.auditLogs.length - 1 && <div className="absolute top-4 left-1.5 w-0.5 h-full bg-gray-100 dark:bg-gray-800"></div>}
                            </div>
                            <div className="pb-4">
                              <p className={`text-xs font-black uppercase tracking-widest ${log.type === 'TAB_SWITCH' ? 'text-red-600' : 'text-gray-700 dark:text-gray-300'}`}>{log.type.replace('_', ' ')}</p>
                              <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 mt-0.5 leading-relaxed">{log.details}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Detailed Response Review */}
                  <div className="space-y-6">
                    {/* Proctoring Snapshot */}
                    {selectedReport.lastSnapshot && (
                      <div className="bg-white dark:bg-gray-900 p-6 rounded-[2rem] border-2 border-gray-50 dark:border-gray-800 shadow-sm space-y-4">
                        <div className="flex justify-between items-center px-2">
                          <h5 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Live Proctor Snapshot</h5>
                          <span className="text-[9px] font-bold text-gray-300">
                            {selectedReport.lastSnapshotTime ? new Date(selectedReport.lastSnapshotTime).toLocaleTimeString() : ''}
                          </span>
                        </div>
                        <div className="aspect-video w-full bg-gray-100 dark:bg-black rounded-xl overflow-hidden border dark:border-gray-700 relative group">
                          <img
                            src={selectedReport.lastSnapshot}
                            alt="Proctor Snapshot"
                            className="w-full h-full object-cover transition-transform group-hover:scale-110"
                          />
                          <div className="absolute top-2 right-2 bg-red-600 text-white text-[9px] font-bold px-2 py-0.5 rounded animate-pulse">
                            LIVE MONITOR
                          </div>
                        </div>
                        <p className="text-[10px] text-center text-gray-400 font-medium">
                          Snapshot captured automatically during assessment.
                        </p>
                      </div>
                    )}

                    <div className="bg-white dark:bg-gray-900 p-6 rounded-[2rem] border-2 border-gray-50 dark:border-gray-800 shadow-sm space-y-4">
                      <h5 className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-2">Detailed Response Review</h5>
                      <div className="max-h-[500px] overflow-y-auto space-y-3 pr-2 custom-scrollbar">
                        {reportQuestions.map((q, i) => {
                          const response = selectedReport.responses?.[q.id];
                          const isCorrect = response === q.correctOption;

                          return (
                            <div key={q.id} className={`p-4 rounded-2xl border transition-all ${isCorrect ? 'bg-green-50/30 dark:bg-green-900/10 border-green-100 dark:border-green-900/30' : 'bg-red-50/30 dark:bg-red-900/10 border-red-100 dark:border-red-900/30'}`}>
                              <div className="flex justify-between items-start mb-2">
                                <span className="text-[9px] font-black text-gray-400 uppercase bg-white dark:bg-gray-800 px-2 py-0.5 rounded shadow-sm">Q {i + 1} • {q.marks}pt</span>
                                {isCorrect ?
                                  <span className="text-green-600 bg-green-100 dark:bg-green-900 px-1.5 py-0.5 rounded text-[10px] font-bold">CORRECT</span> :
                                  <span className="text-red-600 bg-red-100 dark:bg-red-900 px-1.5 py-0.5 rounded text-[10px] font-bold">INCORRECT</span>
                                }
                              </div>
                              <p className="text-[11px] font-bold text-gray-700 dark:text-gray-300 mb-3">{q.text}</p>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="bg-white dark:bg-gray-800 p-2 rounded-xl border border-gray-100 dark:border-gray-700">
                                  <p className="text-[8px] font-black text-gray-400 uppercase mb-0.5">Selected</p>
                                  <p className={`text-xs font-black ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>{response || 'NONE'}</p>
                                </div>
                                {!isCorrect && (
                                  <div className="bg-white dark:bg-gray-800 p-2 rounded-xl border border-gray-100 dark:border-gray-700">
                                    <p className="text-[8px] font-black text-green-600 uppercase mb-0.5">Correct</p>
                                    <p className="text-xs font-black text-green-600">{q.correctOption}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


          {confirmDelete && (
            <div className="fixed inset-0 bg-gray-950/80 backdrop-blur-sm z-[300] flex items-center justify-center p-4 animate-in fade-in duration-300">
              <div className="bg-white dark:bg-gray-900 rounded-[2.5rem] p-10 max-w-sm w-full text-center shadow-2xl transform animate-in zoom-in-95 duration-300 border border-red-50 dark:border-red-900/20">
                <div className="w-16 h-16 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-3xl flex items-center justify-center mx-auto text-3xl mb-4 transition-colors">
                  ⚠️
                </div>
                <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2 transition-colors">{confirmDelete.title}</h3>
                <p className="text-gray-500 dark:text-gray-400 font-medium mb-8 transition-colors text-sm leading-relaxed">{confirmDelete.description}</p>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={async () => {
                      const { id, ids, type } = confirmDelete;
                      setConfirmDelete(null);
                      if (type === 'college') await StorageService.deleteCollege(id);
                      else if (type === 'assessment') await StorageService.deleteAssessment(id);
                      else if (type === 'attempt') await StorageService.deleteAttempt(id);
                      else if (type === 'link') await StorageService.deleteAccessLink(id);
                      else if (type === 'bulk_attempt' && ids) {
                        await Promise.all(ids.map(delId => StorageService.deleteAttempt(delId)));
                        setSelectedReportIds(new Set());
                      }
                      await refreshData(true);
                    }}
                    className="w-full bg-red-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-red-100 dark:shadow-none hover:bg-red-700 transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Confirm Delete
                  </button>
                  <button onClick={() => setConfirmDelete(null)} className="w-full py-2 font-bold text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div >
  );
};

export default AdminDashboard;
